/**
 * src/techHubConfig.js
 *
 * VISINVOKE Tech Hub - central configuration and single source of truth.
 *
 * The Tech Hub is a technician-facing free utility area inside the VISINVOKE
 * platform. It runs only from the platform, behind a signed-in VISINVOKE
 * account that carries Technician Access. There is no offline mode, no local
 * HTML file usage, and no anonymous access.
 *
 * Technician Access is a normal entitlement flag on the signed-in VISINVOKE
 * user object (technician_access). It does NOT create a separate technician
 * account, shop account, partner account, client or device link, managed
 * device relationship, approval workflow, or any RMM / MSP / customer
 * management behaviour. The route is sign-in gated through PrivateRoute; this
 * page handles the content-level Technician Access state.
 *
 * To surface a future tool, add ONE entry to the relevant group in
 * TECH_HUB_GROUPS. Tools marked status 'available' open through the dynamic
 * route /dashboard/utilities/tech-hub/:toolSlug. Tools marked 'pending' render
 * as visible cards that are not yet openable, so the remaining conversion
 * backlog is always clear.
 */

// Base route prefix for every Tech Hub page.
export const TECH_HUB_BASE_ROUTE = '/dashboard/utilities/tech-hub';

// Status badge palette for tool cards.
export const TECH_HUB_STATUS_COLORS = {
  available: '#34d399', // emerald - converted and openable
  pending:   '#fbbf24', // amber - registered, not yet converted
};

export function getTechHubStatusColor(status) {
  return TECH_HUB_STATUS_COLORS[status] || TECH_HUB_STATUS_COLORS.pending;
}

// ---------------------------------------------------------------------------
// Technician Access entitlement seam.
//
// Reads a single backend flag (technician_access) from the signed-in user
// object exactly as the platform reads other entitlements. Returns false for a
// signed-out user or any user without the flag. No local persistence, no
// approval logic.
// ---------------------------------------------------------------------------
export function getTechnicianAccess(user) {
  return !!(user && user.technician_access === true);
}

// ---------------------------------------------------------------------------
// Tool registry. Grouped exactly per the approved Tech Hub content model.
// Service Package Builder and Contract Calculator are intentionally excluded.
// The former USB Tool Launcher is represented only as an External Tool
// Reference placeholder - never an executable launcher.
// ---------------------------------------------------------------------------
export const TECH_HUB_GROUPS = [
  {
    id: 'diagnostic',
    title: 'Diagnostic & Troubleshooting',
    icon: '\u{1F50D}',
    accent: '#3b82f6',
    tools: [
      { slug: 'problem-diagnostic-tree', icon: '\u{1F333}', title: 'Problem Diagnostic Tree', description: 'Guided troubleshooting flow with decision trees for common PC issues, step by step.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:problem-diagnostic-tree', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'common-fixes-reference', icon: '\u{1F4D6}', title: 'Common Fixes Quick Reference', description: 'Searchable reference of common fixes with step by step procedures and time estimates.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:common-fixes-reference', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'error-code-lookup', icon: '\u{1F6D1}', title: 'Error Code Lookup', description: 'Translation of BSOD, POST beep, and Windows error codes with fix procedures.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:error-code-lookup', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'driver-issue-resolver', icon: '\u{1F4BF}', title: 'Driver Issue Resolver', description: 'Identify hardware by Hardware ID and find the correct driver source and steps.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:driver-issue-resolver', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'hardware-compatibility-checker', icon: '\u{1F9E9}', title: 'Hardware Compatibility Checker', description: 'Verify CPU, motherboard, RAM, GPU, and PSU compatibility before ordering parts.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:hardware-compatibility-checker', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'system-bottleneck-analyzer', icon: '\u{1F4C9}', title: 'System Bottleneck Analyzer', description: 'Identify performance limitations and produce upgrade recommendations.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:system-bottleneck-analyzer', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
    ],
  },
  {
    id: 'build-upgrade',
    title: 'Build & Upgrade Tools',
    icon: '\u{1F6E0}',
    accent: '#a855f7',
    tools: [
      { slug: 'pc-build-configurator', icon: '\u{1F5A5}', title: 'PC Build Configurator', description: 'Plan complete PC builds with component selection and a build total for technician planning.', status: 'available', resourceType: 'build-planning-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:pc-build-configurator', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'gaming-pc-calculator', icon: '\u{1F3AE}', title: 'Gaming PC Calculator', description: 'Match specifications to gaming requirements with performance estimates.', status: 'available', resourceType: 'build-planning-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:gaming-pc-calculator', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'workstation-builder', icon: '\u{1F5A5}', title: 'Workstation Builder', description: 'Plan professional workstations for CAD, video, and 3D rendering by workload and tier.', status: 'available', resourceType: 'build-planning-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:workstation-builder', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'upgrade-recommendation', icon: '\u{2B06}', title: 'Upgrade Recommendation', description: 'Analyse an existing system and produce prioritised upgrade guidance.', status: 'available', resourceType: 'build-planning-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:upgrade-recommendation', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'build-spec-sheet', icon: '\u{1F4C4}', title: 'Build Spec Sheet', description: 'Document a build specification and component summary for technician planning.', status: 'available', resourceType: 'build-planning-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:build-spec-sheet', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'refurbished-pricer', icon: '\u{1F4B0}', title: 'Refurbished PC Valuation', description: 'Estimate a refurbishment value from specification, condition, and age. Planning estimate only.', status: 'available', resourceType: 'build-planning-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:refurbished-pricer', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
    ],
  },
  {
    id: 'qa-reports',
    title: 'QA, Testing & Reports',
    icon: '\u{1F9EA}',
    accent: '#22d3ee',
    tools: [
      { slug: 'stress-test-protocol', icon: '\u{1F525}', title: 'Stress Test Protocol', description: 'Record CPU, GPU, memory, storage, and full-system stress test results and a verdict.', status: 'available', resourceType: 'checklist-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:stress-test-protocol', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'burnin-tracker', icon: '\u{1F321}', title: 'Burn-In Tracker', description: 'Log extended burn-in testing with per-program results, temperatures, and a verdict.', status: 'available', resourceType: 'checklist-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:burnin-tracker', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'qc-checklist', icon: '\u{2705}', title: 'QC Checklist', description: 'Work through a quality control checklist with completion status for technician reference.', status: 'available', resourceType: 'checklist-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:qc-checklist', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'repair-checklist', icon: '\u{1F4CB}', title: 'Repair Checklist', description: 'Work through repair-step checklists by repair type with completion tracking.', status: 'available', resourceType: 'checklist-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:repair-checklist', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'before-after-report', icon: '\u{1F4CA}', title: 'Before/After Report', description: 'Record before and after service comparison with boot-time improvement.', status: 'available', resourceType: 'report-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:before-after-report', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'system-info-report', icon: '\u{1F5A5}', title: 'System Information Report', description: 'Document a system hardware and software inventory summary.', status: 'available', resourceType: 'report-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:system-info-report', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'backup-verification', icon: '\u{1F4BE}', title: 'Backup Verification', description: 'Record backup configuration, folders, and a restore-test result for a technician summary.', status: 'available', resourceType: 'report-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:backup-verification', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'customer-handover', icon: '\u{1F4E6}', title: 'Customer Handover', description: 'Handover checklist with completed-work and setup notes for technician reference.', status: 'available', resourceType: 'report-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:customer-handover', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
    ],
  },
  {
    id: 'data-security',
    title: 'Data & Security',
    icon: '\u{1F510}',
    accent: '#f87171',
    tools: [
      { slug: 'data-recovery-quote', icon: '\u{1F4BD}', title: 'Data Recovery Assessment', description: 'Assess recovery difficulty, likelihood, and handling risk from device type and damage, with recommended next steps.', status: 'available', resourceType: 'diagnostic-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:data-recovery-quote', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'security-audit', icon: '\u{1F6E1}', title: 'Security Audit', description: 'Work through a security checklist to produce a risk level and findings summary for technician reference.', status: 'available', resourceType: 'security-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:security-audit', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
      { slug: 'password-policy', icon: '\u{1F511}', title: 'Password Policy Generator', description: 'Generate password-policy guidance (length, complexity, MFA, lockout). Guidance only; no passwords are entered, displayed, or stored in VISINVOKE.', status: 'available', resourceType: 'security-resource', accessLevel: 'technician', availability: 'available-to-technicians', launcherAction: 'run-through-desktop-launcher', launcherPackageId: 'tech-util:password-policy', launchMode: 'desktop-launcher', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in the browser and downloads nothing.', userFacingNotes: 'Technician Access is required to run this resource through the VISINVOKE Desktop App.' },
    ],
  },
  {
    id: 'help',
    title: 'Help',
    icon: '\u{2139}',
    accent: '#a3e635',
    tools: [
      { slug: 'quick-start', icon: '\u{1F680}', title: 'Quick Start', description: 'Getting started with the VISINVOKE Tech Hub for approved Technician Access accounts.', status: 'available', resourceType: 'help-resource', accessLevel: 'technician', availability: 'reference-only', launcherAction: 'show-reference-only', launcherPackageId: 'tech-util:quick-start', launchMode: 'reference', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Reference resource shown in the catalogue. It does not run in the browser.', userFacingNotes: 'Technician Access is required to open this resource through the VISINVOKE Desktop App.' },
      { slug: 'user-guide', icon: '\u{1F4DA}', title: 'User Guide', description: 'Complete guide to the six VISINVOKE Tech Hub tool groups and how they are used in-session.', status: 'available', resourceType: 'help-resource', accessLevel: 'technician', availability: 'reference-only', launcherAction: 'show-reference-only', launcherPackageId: 'tech-util:user-guide', launchMode: 'reference', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Reference resource shown in the catalogue. It does not run in the browser.', userFacingNotes: 'Technician Access is required to open this resource through the VISINVOKE Desktop App.' },
      { slug: 'tech-hub-help', icon: '\u{1F4D8}', title: 'Tech Hub Help', description: 'Help and safe-usage notes for the VISINVOKE Tech Hub, including the technician and client-PC boundary.', status: 'available', resourceType: 'help-resource', accessLevel: 'technician', availability: 'reference-only', launcherAction: 'show-reference-only', launcherPackageId: 'tech-util:tech-hub-help', launchMode: 'reference', licenceStatus: 'VISINVOKE-owned', visibility: 'all-users', safetyNotes: 'Reference resource shown in the catalogue. It does not run in the browser.', userFacingNotes: 'Technician Access is required to open this resource through the VISINVOKE Desktop App.' },
    ],
  },
];

// Resolve a tool by its slug, returning the tool and its owning group.
export function getTechHubTool(slug) {
  for (let g = 0; g < TECH_HUB_GROUPS.length; g++) {
    const group = TECH_HUB_GROUPS[g];
    for (let t = 0; t < group.tools.length; t++) {
      if (group.tools[t].slug === slug) {
        return { tool: group.tools[t], group: group };
      }
    }
  }
  return null;
}

// Flat count of converted (available) tools, for summary display.
export function countAvailableTools() {
  let n = 0;
  for (let g = 0; g < TECH_HUB_GROUPS.length; g++) {
    const tools = TECH_HUB_GROUPS[g].tools;
    for (let t = 0; t < tools.length; t++) {
      if (tools[t].status === 'available') n++;
    }
  }
  return n;
}

// ---------------------------------------------------------------------------
// Launcher-state resolver (correction stage). Tech Utilities are launcher-run
// resources through the VISINVOKE Desktop App; nothing runs in the browser.
// This maps a resource plus the viewer's Technician Access into the UI action
// state shown on catalogue cards and the resource-detail page. It never
// executes anything and never references a Desktop App bridge that does not
// exist yet - it returns display metadata only.
//
// tone values: 'locked' (no access), 'launcher' (technician, runnable via the
// Desktop App), 'reference' (technician, reference-only resource).
// ---------------------------------------------------------------------------
export function getLauncherState(tool, hasAccess) {
  const availability = tool && tool.availability ? tool.availability : 'available-to-technicians';
  const action = tool && tool.launcherAction ? tool.launcherAction : 'run-through-desktop-launcher';

  if (!hasAccess) {
    return {
      tone: 'locked',
      techUnlocked: false,
      label: 'Technician Access Required',
      sub: 'Request Technician Access',
      color: '#fbbf24',
    };
  }
  if (availability === 'reference-only' || action === 'show-reference-only') {
    return {
      tone: 'reference',
      techUnlocked: true,
      label: 'Open in Desktop Command Center',
      sub: 'Reference resource',
      color: '#38bdf8',
    };
  }
  if (availability === 'licence-review-required' || action === 'licence-review-required') {
    return {
      tone: 'reference',
      techUnlocked: true,
      label: 'Licence Review Required',
      sub: 'Review before use',
      color: '#fb923c',
    };
  }
  return {
    tone: 'launcher',
    techUnlocked: true,
    label: 'Available in Desktop Command Center',
    sub: 'Open in Desktop Command Center',
    color: '#34d399',
  };
}

// ---------------------------------------------------------------------------
// Converted tool dataset: Common Fixes Quick Reference.
// Faithful transcription of the source reference data.
// ---------------------------------------------------------------------------
export const COMMON_FIXES = [
  { title: 'Slow Boot Times', time: '10-15 min', steps: ['Check Startup programs in Task Manager - disable unnecessary', 'Run Disk Cleanup - free up space', 'Check for pending Windows Updates', 'Disable Fast Startup if causing issues', 'Check for malware with MalwareBytes'] },
  { title: 'No Internet Connection', time: '5-10 min', steps: ['ipconfig /release then ipconfig /renew', 'netsh winsock reset', 'netsh int ip reset', 'ipconfig /flushdns', 'Restart router and modem', 'Update network adapter driver'] },
  { title: '100% Disk Usage', time: '10-20 min', steps: ['Disable Windows Search temporarily', 'Disable SuperFetch service', 'Check for Windows Updates running', 'Run chkdsk /f /r', 'Check for malware', 'Consider SSD upgrade if HDD'] },
  { title: 'Audio Not Working', time: '5-10 min', steps: ['Check volume not muted', 'Right-click speaker icon then Troubleshoot', 'Check correct playback device selected', 'Update audio drivers', 'Restart Windows Audio service', 'Check audio enabled in BIOS'] },
  { title: 'Windows Update Stuck', time: '15-30 min', steps: ['Wait 2-3 hours first (may be installing)', 'Stop Windows Update service', 'Delete C:\\Windows\\SoftwareDistribution', 'Restart Windows Update service', 'Run Windows Update Troubleshooter', 'Manually download and install update'] },
  { title: 'Black Screen After Login', time: '10-15 min', steps: ['Ctrl+Shift+Esc to open Task Manager', 'File then Run New Task then explorer.exe', 'Boot to Safe Mode', 'Run SFC scan', 'Check for graphics driver issues', 'System Restore to before issue'] },
  { title: 'WiFi Keeps Disconnecting', time: '10-15 min', steps: ['Update WiFi adapter driver', 'Disable power saving for WiFi adapter', 'Change WiFi channel on router', 'Forget network and reconnect', 'Reset network settings', 'Check for interference from other devices'] },
  { title: 'USB Device Not Recognized', time: '5-10 min', steps: ['Try different USB port', 'Try USB 2.0 port instead of 3.0', 'Device Manager then Uninstall USB controllers, restart', 'Update USB drivers', 'Disable USB selective suspend', 'Check device works on another computer'] },
  { title: 'High CPU Usage', time: '10-20 min', steps: ['Check Task Manager for culprit process', 'Update all drivers', 'Scan for malware', 'Disable unnecessary startup programs', 'Check for Windows Updates', 'Clean dust from CPU cooler'] },
  { title: 'PC Won\u0027t Sleep', time: '5-10 min', steps: ['powercfg /requests (shows what is preventing sleep)', 'Disable wake timers in Power Options', 'Update network adapter driver', "Disable 'Allow device to wake computer'", 'Check scheduled tasks', 'Run powercfg /devicequery wake_armed'] },
  { title: 'Printer Not Printing', time: '10-15 min', steps: ['Check printer queue - clear if stuck', 'Restart Print Spooler service', 'Set as default printer', 'Update printer driver', 'Remove and re-add printer', 'Check cable or wireless connection'] },
  { title: 'Microsoft Store Not Working', time: '10-15 min', steps: ['Reset Microsoft Store cache: wsreset.exe', 'Run Windows Store Apps Troubleshooter', 'Re-register Store via PowerShell as admin', 'Check date and time settings correct', 'Sign out and sign in to Microsoft account'] },
  { title: 'File Explorer Slow/Freezing', time: '5-10 min', steps: ['Clear File Explorer history', 'Disable Quick Access', "Folder Options then View then uncheck 'Show preview handlers'", 'Run Disk Cleanup', 'Check for corrupt system files: sfc /scannow', 'Restart explorer.exe'] },
  { title: 'Windows Activation Error', time: '5-10 min', steps: ['Settings then Update and Security then Activation then Troubleshoot', 'slmgr /rearm (extends trial)', 'Check internet connection', 'Run: slmgr /ato (reactivate)', 'Check hardware has not changed significantly', 'Contact Microsoft support if genuine'] },
  { title: 'Corrupted Recycle Bin', time: '5 min', steps: ['Empty Recycle Bin', 'Close all programs', 'Open Command Prompt as admin', 'Run: rd /s /q C:\\$Recycle.bin', 'Restart computer', 'Recycle bin will be recreated automatically'] },
];

// ---------------------------------------------------------------------------
// Converted tool dataset: Problem Diagnostic Tree.
// Faithful transcription of all eight problem categories. The legacy tool
// persisted a diagnostic history to localStorage; that behaviour is removed.
// ---------------------------------------------------------------------------
export const DIAGNOSTIC_CATEGORIES = [
  { key: 'no-boot',          icon: '\u{1F6AB}', name: "Won't Boot / Power" },
  { key: 'slow-performance', icon: '\u{1F40C}', name: 'Slow Performance' },
  { key: 'blue-screen',      icon: '\u{1F499}', name: 'Blue Screen (BSOD)' },
  { key: 'overheating',      icon: '\u{1F525}', name: 'Overheating' },
  { key: 'no-display',       icon: '\u{1F5A5}', name: 'No Display' },
  { key: 'internet',         icon: '\u{1F310}', name: 'Internet Issues' },
  { key: 'audio',            icon: '\u{1F50A}', name: 'Audio Problems' },
  { key: 'freezing',         icon: '\u{2744}',  name: 'Freezing/Hanging' },
];

export const DIAGNOSTICS = {
  'no-boot': {
    title: "PC Won't Boot / Power Issues",
    steps: [
      { number: 1, title: 'Check Power Connections', content: 'Verify power cable is securely connected to both wall outlet and PSU. Check PSU switch is ON. Test with different power cable if available.', tools: ['Power cable', 'Multi-meter (optional)'] },
      { number: 2, title: 'Listen for Beep Codes', content: 'Power on the PC and listen carefully for beep patterns from motherboard speaker. Document the pattern.', decision: { question: 'Do you hear any beep codes?', yes: 'Beep codes indicate specific hardware failure. Use Error Code Lookup tool to identify the issue.', no: 'Continue to next step.' } },
      { number: 3, title: 'Check PSU', content: "Test PSU using paperclip test (short green wire to black ground). If PSU fan doesn't spin, PSU is likely dead.", decision: { question: 'Does PSU fan spin?', yes: 'PSU likely OK. Continue to next step.', no: 'Replace PSU. Common failure point.' } },
      { number: 4, title: 'Reseat RAM', content: 'Remove all RAM sticks. Clean contacts with isopropyl alcohol. Reinstall ONE stick in slot A1. Try booting.', decision: { question: 'Does PC boot with one RAM stick?', yes: 'RAM issue found. Test each stick individually to find faulty one.', no: 'Continue to next step.' } },
      { number: 5, title: 'Test with Minimum Hardware', content: 'Disconnect all non-essential hardware: GPU, extra drives, USB devices. Leave only: CPU, 1 RAM stick, PSU, motherboard.', decision: { question: 'Does PC boot now?', yes: 'One of the removed components is faulty. Reconnect one at a time to identify.', no: 'Likely motherboard or CPU failure. Professional diagnosis needed.' } },
    ],
    commonSolutions: ['Dead PSU (35% of cases)', 'Faulty RAM (25% of cases)', 'Loose cables/connections (20% of cases)', 'Failed motherboard (15% of cases)', 'CPU/Socket issue (5% of cases)'],
  },
  'slow-performance': {
    title: 'Slow Performance Diagnosis',
    steps: [
      { number: 1, title: 'Check Resource Usage', content: 'Open Task Manager (Ctrl+Shift+Esc). Check CPU, RAM, and Disk usage. Document which resources are maxed out.', tools: ['Task Manager', 'Resource Monitor'] },
      { number: 2, title: 'Identify High Usage Processes', content: 'Sort by CPU/Memory usage in Task Manager. Note any unexpected or unknown processes consuming resources.', decision: { question: 'Is there suspicious software using resources?', yes: 'May be malware. Run virus scan with MalwareBytes from USB stick.', no: 'Continue to next step.' } },
      { number: 3, title: 'Check Disk Health', content: 'Run CrystalDiskInfo from USB. Check SMART status and health percentage. Failing drives cause severe slowdowns.', decision: { question: 'Is disk health good (>90%)?', yes: 'Disk OK. Continue to next step.', no: 'Clone/backup immediately. Replace drive. Failing drives cause 40% of slowness issues.' } },
      { number: 4, title: 'Check for 100% Disk Usage', content: 'If Task Manager shows constant 100% disk usage, this is often Windows Update, SuperFetch, or antivirus scanning.', decision: { question: 'Is disk usage at 100%?', yes: 'Disable SuperFetch, check Windows Update status, temporary disable antivirus to test.', no: 'Continue to next step.' } },
      { number: 5, title: 'Check RAM Usage', content: 'If RAM is maxed out (>90%), system will use slow page file on disk. Check for memory leaks or insufficient RAM.', decision: { question: 'Is RAM usage >85%?', yes: 'Recommend RAM upgrade. Close unnecessary programs. Check for memory leaks.', no: 'Check startup programs and services.' } },
      { number: 6, title: 'Disable Unnecessary Startup Programs', content: "Open Task Manager then Startup tab. Disable programs not needed at startup. Typical savings: 30-60 seconds boot time.", tools: ['Task Manager', 'MSConfig'] },
    ],
    commonSolutions: ['Failing HDD - Upgrade to SSD (40% of cases)', 'Insufficient RAM (25% of cases)', 'Malware/bloatware (20% of cases)', 'Too many startup programs (10% of cases)', 'Overheating throttling (5% of cases)'],
  },
  'blue-screen': {
    title: 'Blue Screen of Death (BSOD) Diagnosis',
    steps: [
      { number: 1, title: 'Capture Error Code', content: 'Note the STOP code (for example CRITICAL_PROCESS_DIED, SYSTEM_SERVICE_EXCEPTION). Take photo if needed.', tools: ['Phone camera', 'BlueScreenView from USB'] },
      { number: 2, title: 'Check Recent Changes', content: 'Ask the user: New hardware installed? Driver updates? Windows updates? New software? BSODs often start after changes.', decision: { question: 'Were there recent changes before BSODs started?', yes: 'Undo recent changes. Roll back drivers. Uninstall new software. Restart.', no: 'Continue systematic diagnosis.' } },
      { number: 3, title: 'Analyze Dump Files', content: 'Use BlueScreenView to analyze C:\\Windows\\Minidump files. Identify which driver/file caused crash.', tools: ['BlueScreenView (USB)', 'WinDbg (advanced)'] },
      { number: 4, title: 'Common BSOD Causes', content: 'Driver issues (60%), RAM problems (20%), Overheating (10%), Disk errors (5%), PSU instability (5%)', decision: { question: 'Is the same driver listed in multiple crashes?', yes: 'Update or roll back that specific driver. Check manufacturer website.', no: 'Continue with hardware testing.' } },
      { number: 5, title: 'Test RAM', content: 'Run MemTest86 from USB (minimum 4 passes, overnight test). Faulty RAM causes random BSODs.', decision: { question: 'Did MemTest find errors?', yes: 'Replace faulty RAM module(s). Test each stick individually.', no: 'RAM OK. Check other hardware.' } },
      { number: 6, title: 'Check Temperatures', content: 'Use HWiNFO64 to monitor temps under load. CPU >85C or GPU >90C causes thermal throttling and crashes.', decision: { question: 'Are temperatures in safe range?', yes: 'Thermals OK. Check disk and PSU.', no: 'Clean dust, reapply thermal paste, check fan operation.' } },
    ],
    commonSolutions: ['Outdated/corrupt drivers (60% of cases)', 'Faulty RAM (20% of cases)', 'Overheating (10% of cases)', 'Failing hard drive (5% of cases)', 'PSU voltage instability (5% of cases)'],
  },
  'overheating': {
    title: 'Overheating Diagnosis',
    steps: [
      { number: 1, title: 'Establish Baseline Temperatures', content: 'Use HWiNFO64 to record idle and load temps. CPU: Safe <80C, Warn 80-90C, Critical >90C. GPU: Safe <85C, Warn 85-95C, Critical >95C.', tools: ['HWiNFO64', 'Core Temp', 'GPU-Z'] },
      { number: 2, title: 'Visual Inspection', content: 'Open case and inspect: Dust buildup on heatsinks/fans? All fans spinning? Cable blocking airflow? Thermal paste dried out?', tools: ['Compressed air', 'Flashlight'] },
      { number: 3, title: 'Clean Dust and Debris', content: 'Use compressed air to clean all heatsinks, fans, and vents. Pay special attention to CPU cooler fins and GPU fans.', decision: { question: 'Was there significant dust buildup?', yes: 'Clean thoroughly. Retest temperatures. Often fixes overheating (50% of cases).', no: 'Continue to next step.' } },
      { number: 4, title: 'Check Fan Operation', content: 'Verify all fans spinning: CPU cooler, GPU, case fans, PSU fan. Check fan speeds in BIOS/HWiNFO64.', decision: { question: 'Are all fans working properly?', yes: 'Fans OK. Continue to thermal paste check.', no: 'Replace non-functioning fans. Check fan headers/cables.' } },
      { number: 5, title: 'Thermal Paste Application', content: 'Remove CPU cooler. Clean old paste with isopropyl alcohol. Apply new thermal paste (pea-sized dot in center). Remount cooler firmly.', tools: ['Thermal paste', 'Isopropyl alcohol', 'Lint-free cloth'], warning: 'Dried thermal paste causes 30% of overheating issues. Replace every 3-4 years.' },
      { number: 6, title: 'Check Airflow', content: 'Ensure proper airflow: Front fans intake, rear/top fans exhaust. Remove any airflow obstructions. Consider cable management.', decision: { question: 'Is airflow path clear and logical?', yes: 'Airflow OK. Consider additional cooling if still overheating.', no: 'Reorganize fan configuration and cable routing.' } },
    ],
    commonSolutions: ['Dust buildup (50% of cases)', 'Dried thermal paste (30% of cases)', 'Failed/slow fans (10% of cases)', 'Poor case airflow (5% of cases)', 'Inadequate cooling solution (5% of cases)'],
  },
  'no-display': {
    title: 'No Display Signal Diagnosis',
    steps: [
      { number: 1, title: 'Verify Power', content: 'Confirm PC is actually turning on: Fans spinning? LEDs lit? HDD/SSD activity light? Listen for POST beep.', decision: { question: 'Is PC powering on?', yes: 'Power OK. Continue display diagnosis.', no: 'Use the Won\u0027t Boot diagnostic instead.' } },
      { number: 2, title: 'Check Monitor and Cables', content: 'Test monitor with different device. Try different video cable. Ensure cable firmly connected to both PC and monitor.', decision: { question: 'Does monitor work with different device?', yes: 'Monitor OK. Issue is with PC video output.', no: 'Monitor or cable faulty. Replace and retest.' } },
      { number: 3, title: 'Check Input Source', content: 'Verify monitor is set to correct input (HDMI, DisplayPort, DVI, VGA). Try auto-detect if available.', tools: ['Monitor menu button'] },
      { number: 4, title: 'Test Different Video Output', content: 'If PC has both integrated and discrete GPU, try both. Move cable between motherboard ports and GPU ports.', decision: { question: 'Does display work on different port?', yes: 'Original port/GPU may be faulty. Document findings.', no: 'Continue with GPU troubleshooting.' } },
      { number: 5, title: 'Reseat GPU', content: 'Power off, unplug. Remove GPU completely. Clean PCIe slot and GPU contacts. Reinstall GPU firmly. Reconnect PCIe power cables.', decision: { question: 'Does display work after reseating?', yes: 'Loose GPU was the issue. Secure and document.', no: 'Continue testing.' } },
      { number: 6, title: 'Test with Known-Good GPU', content: 'If available, test with different GPU or use integrated graphics (remove discrete GPU, connect to motherboard port).', decision: { question: 'Does display work with different GPU?', yes: 'Original GPU faulty. Recommend replacement.', no: 'Possible motherboard/PCIe slot issue.' } },
    ],
    commonSolutions: ['Loose video cable connection (30% of cases)', 'Wrong monitor input selected (20% of cases)', 'Faulty video cable (15% of cases)', 'Loose GPU in slot (15% of cases)', 'Failed GPU (10% of cases)', 'Monitor failure (10% of cases)'],
  },
  'internet': {
    title: 'Internet Connectivity Issues',
    steps: [
      { number: 1, title: 'Isolate the Problem', content: 'Test other devices on same network. Check if issue is single PC or entire network.', decision: { question: 'Do other devices have internet?', yes: 'Issue is this PC only. Continue PC-specific diagnosis.', no: 'Network/router issue. Restart modem and router, contact ISP if persists.' } },
      { number: 2, title: 'Check Physical Connection', content: 'WiFi: Signal strength? Ethernet: Cable plugged in securely? Link lights on? Try different cable/port.', tools: ['Ethernet cable tester (if available)'] },
      { number: 3, title: 'Run Network Diagnostics', content: 'Windows Network Troubleshooter: Settings then Network and Internet then Status then Troubleshoot. Document any errors found.', decision: { question: 'Did troubleshooter fix the issue?', yes: 'Document what was fixed. Test connection.', no: 'Continue manual diagnosis.' } },
      { number: 4, title: 'Check IP Configuration', content: 'Command Prompt: ipconfig /all. Verify: Valid IP address (not 169.254.x.x), Default gateway present, DNS servers configured.', decision: { question: 'Is IP address valid (not 169.254.x.x)?', yes: 'IP config OK. Continue to DNS check.', no: 'Run: ipconfig /release then ipconfig /renew. If fails, check DHCP.' } },
      { number: 5, title: 'Test DNS Resolution', content: 'Ping test: ping 8.8.8.8 (tests connectivity), ping google.com (tests DNS). Compare results.', decision: { question: 'Does 8.8.8.8 ping but google.com fails?', yes: 'DNS issue. Change DNS to 8.8.8.8 and 8.8.4.4 (Google DNS).', no: 'If both fail, connectivity issue. If both work, browser problem.' } },
      { number: 6, title: 'Reset Network Stack', content: 'Admin Command Prompt: netsh winsock reset, netsh int ip reset, ipconfig /flushdns. Restart computer.', warning: 'This resets all network settings. Document current config first.' },
      { number: 7, title: 'Check Network Adapter', content: 'Device Manager: Network adapters. Check for yellow exclamation marks. Update/reinstall drivers if needed.', decision: { question: 'Are there driver errors?', yes: 'Uninstall adapter, restart PC (Windows will reinstall). Or update from manufacturer.', no: 'Check Windows Firewall and antivirus settings.' } },
    ],
    commonSolutions: ['DNS issues (25% of cases)', 'Corrupt network settings (20% of cases)', 'Outdated/corrupt network drivers (20% of cases)', 'Router/modem issues (15% of cases)', 'IP conflict or DHCP failure (10% of cases)', 'Firewall/antivirus blocking (10% of cases)'],
  },
  'audio': {
    title: 'Audio Problems Diagnosis',
    steps: [
      { number: 1, title: 'Verify Physical Connections', content: 'Check speakers/headphones plugged into correct jack (green = output, pink = mic). Try front and rear panel ports.', decision: { question: 'Are audio devices connected properly?', yes: 'Connections OK. Continue testing.', no: 'Connect to correct port and retest.' } },
      { number: 2, title: 'Check Volume Levels', content: 'System tray volume not muted? Volume mixer: all applications unmuted? Physical speaker volume turned up?', tools: ['Volume Mixer (right-click speaker icon)'] },
      { number: 3, title: 'Test with Different Speakers/Headphones', content: 'Connect known-working audio device. This isolates hardware vs software issue.', decision: { question: 'Does different audio device work?', yes: 'Original speakers/headphones faulty.', no: 'PC audio output issue. Continue diagnosis.' } },
      { number: 4, title: 'Check Default Playback Device', content: 'Right-click speaker icon then Sounds then Playback tab. Verify correct device set as default. Test device.', decision: { question: 'Is correct device selected and showing activity?', yes: 'Device configured correctly. Check drivers.', no: 'Set correct device as default and test.' } },
      { number: 5, title: 'Update/Reinstall Audio Drivers', content: 'Device Manager then Sound, video and game controllers. Uninstall audio device, restart PC. Windows will reinstall drivers.', tools: ['Device Manager', 'Manufacturer website for latest drivers'] },
      { number: 6, title: 'Check Audio Services', content: 'Services.msc: Verify Windows Audio and Windows Audio Endpoint Builder are running and set to Automatic.', decision: { question: 'Are audio services running?', yes: 'Services OK. Check BIOS settings.', no: 'Start services and set to Automatic startup.' } },
      { number: 7, title: 'BIOS Audio Settings', content: 'Restart and enter BIOS. Check if onboard audio is enabled. Some boards have a High Definition Audio option.', warning: 'HD Audio must be enabled in BIOS for audio to work.' },
    ],
    commonSolutions: ['Wrong playback device selected (30% of cases)', 'Muted volume or zero volume level (25% of cases)', 'Corrupt audio drivers (20% of cases)', 'Faulty speakers/headphones (15% of cases)', 'Disabled audio in BIOS (5% of cases)', 'Failed audio chip (5% of cases)'],
  },
  'freezing': {
    title: 'System Freezing/Hanging Diagnosis',
    steps: [
      { number: 1, title: 'Identify Freeze Pattern', content: 'When does freezing occur? During boot? After login? While using specific programs? Random? Document pattern.', decision: { question: 'Does freeze happen during specific activity?', yes: 'Isolate that activity. May be software/driver conflict.', no: 'Random freezing suggests hardware issue.' } },
      { number: 2, title: 'Check Event Viewer', content: 'Event Viewer then Windows Logs then System. Look for Critical/Error events around freeze time. Note error codes.', tools: ['Event Viewer', 'Paper to note error codes'] },
      { number: 3, title: 'Test in Safe Mode', content: 'Boot to Safe Mode (hold Shift while clicking Restart then Troubleshoot then Advanced then Startup Settings then Safe Mode).', decision: { question: 'Does freezing still occur in Safe Mode?', yes: 'Hardware issue likely. Test RAM, drives, temps.', no: 'Software/driver issue. Clean boot to isolate.' } },
      { number: 4, title: 'Test RAM Thoroughly', content: 'Run MemTest86 overnight (minimum 8 passes). Faulty RAM is the number one cause of random freezing.', decision: { question: 'Did MemTest find errors?', yes: 'Replace faulty RAM. Test each module individually.', no: 'RAM OK. Check drive health.' } },
      { number: 5, title: 'Check Drive Health', content: 'CrystalDiskInfo: Check SMART status. Run disk check: chkdsk /f /r. Failing drives cause freezing during read/write.', decision: { question: 'Is drive health good?', yes: 'Drive OK. Check temperatures.', no: 'Clone/backup immediately. Replace drive.' } },
      { number: 6, title: 'Monitor Temperatures Under Load', content: 'Use HWiNFO64 during freeze-prone activity. CPU >90C causes thermal throttling and freezing.', decision: { question: 'Do temps spike before freezing?', yes: 'Overheating causing freezes. Clean system, reapply thermal paste.', no: 'Check for driver conflicts and malware.' } },
      { number: 7, title: 'Clean Boot Test', content: 'MSConfig: Selective startup, disable all services except Microsoft, disable all startup items. Restart and test.', decision: { question: 'Does freezing stop with clean boot?', yes: 'Re-enable services/startup items one by one to identify culprit.', no: 'Likely hardware issue. Deep hardware diagnostic needed.' } },
    ],
    commonSolutions: ['Faulty RAM (35% of cases)', 'Failing hard drive (25% of cases)', 'Overheating (15% of cases)', 'Driver conflicts (10% of cases)', 'Malware (10% of cases)', 'PSU instability (5% of cases)'],
  },
};

// ---------------------------------------------------------------------------
// Converted tool dataset: Error Code Lookup.
// Faithful transcription of the source error database across four categories.
// Search matches code, name, and description. The category tabs and expandable
// detail behaviour are preserved by the hosting component.
// ---------------------------------------------------------------------------
export const ERROR_CATEGORIES = [
  { key: 'all',      label: 'All Errors' },
  { key: 'bsod',     label: 'BSOD Errors' },
  { key: 'post',     label: 'POST Beep Codes' },
  { key: 'windows',  label: 'Windows Errors' },
  { key: 'hardware', label: 'Hardware Errors' },
];

export const ERROR_DATABASE = {
  bsod: [
    { code: '0x0000000A', name: 'IRQL_NOT_LESS_OR_EQUAL', description: 'A kernel-mode process or driver attempted to access a memory location without permission, or at an invalid IRQL.', causes: ['Faulty or incompatible drivers', 'Faulty RAM', 'Incompatible hardware', 'Corrupt system files'], solutions: ['Update all device drivers, especially network and graphics drivers', 'Run Windows Memory Diagnostic or MemTest86', 'Check for Windows updates', 'Boot in Safe Mode and uninstall recently installed software/drivers', 'Run SFC scan: sfc /scannow', 'Check for BIOS updates'], frequency: 'Common - 15% of BSODs' },
    { code: '0x0000001E', name: 'KMODE_EXCEPTION_NOT_HANDLED', description: 'A kernel-mode program generated an exception which the error handler did not catch.', causes: ['Hardware incompatibility', 'Faulty device drivers', 'Defective RAM', 'Hard drive errors'], solutions: ['Identify the driver causing the issue (check minidump files)', 'Update or roll back problematic drivers', 'Test RAM with MemTest86', 'Check hard drive with CrystalDiskInfo and chkdsk /f /r', 'Disable overclocking', 'Update BIOS firmware'], frequency: 'Moderate - 8% of BSODs' },
    { code: '0x00000050', name: 'PAGE_FAULT_IN_NONPAGED_AREA', description: 'Invalid system memory was referenced. The system tried to access memory that does not exist.', causes: ['Faulty RAM', 'Bad hard drive sectors', 'Defective drivers', 'Antivirus conflicts'], solutions: ['Run MemTest86 for at least 8 passes', 'Test hard drive health with manufacturer tools', 'Update or uninstall problematic drivers', 'Temporarily disable antivirus and test', 'Check for overheating issues', 'Reseat RAM modules'], frequency: 'Common - 12% of BSODs' },
    { code: '0x0000007B', name: 'INACCESSIBLE_BOOT_DEVICE', description: 'Windows lost access to the system partition during startup.', causes: ['Boot sector virus', 'Incompatible storage controller drivers', 'Corrupt file system', 'Failed hard drive'], solutions: ['Boot into Safe Mode and update storage controller drivers', 'Run chkdsk /f /r from recovery console', 'Check BIOS SATA mode (AHCI vs IDE)', 'Repair boot sector with bootrec commands', 'Check drive health with CrystalDiskInfo', 'Clone to new drive if failing'], frequency: 'Moderate - 7% of BSODs' },
    { code: '0x0000007E', name: 'SYSTEM_THREAD_EXCEPTION_NOT_HANDLED', description: 'A system thread generated an exception that the error handler did not catch.', causes: ['Faulty device drivers', 'Hardware conflicts', 'Corrupt system files', 'Incompatible software'], solutions: ['Identify driver in crash dump (use BlueScreenView)', 'Update or uninstall problematic driver', 'Run Driver Verifier to test drivers', 'Run SFC: sfc /scannow', 'Update Windows and BIOS', 'Check for hardware conflicts in Device Manager'], frequency: 'Common - 10% of BSODs' },
    { code: '0x0000009F', name: 'DRIVER_POWER_STATE_FAILURE', description: 'Driver is in an inconsistent or invalid power state.', causes: ['Power management conflicts', 'Outdated drivers', 'USB device issues', 'Faulty PSU'], solutions: ['Update chipset and USB drivers', 'Disable USB selective suspend in Power Options', 'Update BIOS', 'Test with different PSU if available', 'Disable fast startup in Power Options', 'Remove all USB devices and test'], frequency: 'Moderate - 6% of BSODs' },
    { code: '0x000000D1', name: 'DRIVER_IRQL_NOT_LESS_OR_EQUAL', description: 'Driver attempted to access pageable memory at too high an IRQL.', causes: ['Faulty network drivers', 'Incompatible drivers', 'Defective RAM', 'Malware'], solutions: ['Update network adapter drivers', 'Run virus/malware scan', 'Test RAM with MemTest86', 'Boot to Safe Mode with Networking and test', 'Roll back recent driver updates', 'Disable IPv6 and test'], frequency: 'Common - 13% of BSODs' },
    { code: '0x000000EA', name: 'THREAD_STUCK_IN_DEVICE_DRIVER', description: 'Device driver is stuck in an infinite loop waiting for hardware.', causes: ['Faulty graphics drivers', 'Overclocked GPU', 'Hardware failure', 'Overheating'], solutions: ['Update or roll back graphics drivers', 'Use DDU to completely remove graphics drivers, reinstall', 'Reset GPU overclock to stock', 'Check GPU temperatures', 'Test with different GPU if available', 'Check for GPU VRAM errors'], frequency: 'Moderate - 8% of BSODs' },
    { code: '0x000000F4', name: 'CRITICAL_OBJECT_TERMINATION', description: 'A process or thread crucial to system operation has unexpectedly exited.', causes: ['Failing hard drive', 'Corrupt system files', 'Malware', 'Faulty RAM'], solutions: ['Run chkdsk /f /r immediately', 'Check drive health with CrystalDiskInfo', 'Run full virus scan', 'Test RAM with MemTest86', 'Run SFC and DISM scans', 'Backup data and consider drive replacement'], frequency: 'Moderate - 7% of BSODs' },
    { code: '0x00000124', name: 'WHEA_UNCORRECTABLE_ERROR', description: 'Fatal hardware error detected by Windows Hardware Error Architecture.', causes: ['Failing CPU', 'Overheating', 'Unstable overclock', 'PSU failure', 'Motherboard issues'], solutions: ['Check CPU temperatures immediately', 'Reset all overclocking to default', 'Update BIOS', 'Test with different PSU', 'Reseat CPU and check for bent pins', 'May indicate CPU failure - test with known good CPU'], frequency: 'Serious - Hardware failure indicated - 5% of BSODs' },
    { code: 'DPC_WATCHDOG_VIOLATION', name: 'DPC_WATCHDOG_VIOLATION', description: 'Extended deferred procedure call (DPC) timeout, usually driver or hardware issue.', causes: ['Outdated drivers (especially SSD/NVMe)', 'Firmware issues', 'Hardware conflicts'], solutions: ['Update chipset drivers', 'Update SSD/NVMe firmware', 'Update SATA controller drivers', 'Check for BIOS updates', 'Disable C-States in BIOS', 'Test with storage device in different port'], frequency: 'Common on newer systems - 9% of BSODs' },
    { code: 'SYSTEM_SERVICE_EXCEPTION', name: 'SYSTEM_SERVICE_EXCEPTION', description: 'Exception in system service, usually caused by faulty drivers or software.', causes: ['Graphics drivers', 'Antivirus conflicts', 'System file corruption', 'Third-party software'], solutions: ['Update graphics drivers (or roll back)', 'Temporarily disable antivirus', 'Boot to Safe Mode and uninstall recent software', 'Run SFC scan', 'Check Windows Update', 'Use System Restore if recently started'], frequency: 'Very Common - 14% of BSODs' },
    { code: 'VIDEO_TDR_FAILURE', name: 'VIDEO_TDR_FAILURE', description: 'Graphics driver failed to respond within the timeout period.', causes: ['Outdated graphics drivers', 'Overclocked GPU', 'GPU overheating', 'Failing GPU'], solutions: ['Update GPU drivers from manufacturer website', 'Use DDU in Safe Mode, then reinstall drivers', 'Reset GPU overclock', 'Check GPU temperatures (should be under 85C)', 'Clean GPU fans and heatsink', 'Test GPU in different PCIe slot'], frequency: 'Common - 11% of BSODs' },
    { code: 'CRITICAL_PROCESS_DIED', name: 'CRITICAL_PROCESS_DIED', description: 'A critical Windows process stopped working.', causes: ['Corrupt system files', 'Faulty RAM', 'Malware', 'Driver conflicts'], solutions: ['Run SFC: sfc /scannow', 'Run DISM: DISM /Online /Cleanup-Image /RestoreHealth', 'Run MemTest86', 'Full virus scan', 'Check recent Windows Updates (roll back if needed)', 'System Restore to before issue started'], frequency: 'Common - 10% of BSODs' },
    { code: 'MEMORY_MANAGEMENT', name: 'MEMORY_MANAGEMENT', description: 'The memory manager encountered a problem, usually RAM-related.', causes: ['Faulty RAM', 'RAM timing issues', 'Motherboard issues', 'Corrupt page file'], solutions: ['Run MemTest86 for minimum 8 passes', 'Test RAM sticks individually', 'Reset RAM to default XMP/DOCP settings', 'Update BIOS', 'Delete and recreate page file', 'Reseat RAM modules'], frequency: 'Common - 12% of BSODs' },
  ],
  post: [
    { code: '1 short beep', name: 'Normal POST - System OK', description: 'Single beep indicates successful POST (Power-On Self Test).', pattern: 'BEEP', causes: ['This is normal - no error'], solutions: ['No action needed - system is healthy'] },
    { code: '2 short beeps', name: 'POST Error - Check Screen', description: 'POST error occurred, details should be displayed on screen.', pattern: 'BEEP BEEP', causes: ['Various errors - check monitor display', 'CMOS/BIOS settings issue'], solutions: ['Read error message on screen', 'Enter BIOS and check settings', 'Reset BIOS to defaults', 'Check CMOS battery'] },
    { code: '1 long, 1 short beep', name: 'Motherboard Problem', description: 'Motherboard or DRAM refresh failure.', pattern: 'BEEEEEP BEEP', causes: ['Motherboard failure', 'DRAM refresh failure', 'Faulty RAM slots'], solutions: ['Reset BIOS/CMOS', 'Remove and reseat RAM', 'Test with minimal hardware', 'May need motherboard replacement'] },
    { code: '1 long, 2 short beeps', name: 'Video Card Error', description: 'Display adapter failure (graphics card or onboard graphics).', pattern: 'BEEEEEP BEEP BEEP', causes: ['Faulty graphics card', 'Loose GPU', 'Monitor connection issue', 'Graphics BIOS error'], solutions: ['Reseat graphics card firmly', 'Check monitor cable connection', 'Try different graphics card', 'Use integrated graphics if available', 'Clear CMOS'] },
    { code: '1 long, 3 short beeps', name: 'Video Memory Problem', description: 'Video card memory test failure.', pattern: 'BEEEEEP BEEP BEEP BEEP', causes: ['Defective video card RAM', 'Overheating GPU', 'Failed GPU memory chips'], solutions: ['Test with different graphics card', 'Check GPU cooling', 'Reset GPU overclock', 'Graphics card likely needs replacement'] },
    { code: '3 short beeps', name: 'Memory Error', description: 'Base 64K RAM failure.', pattern: 'BEEP BEEP BEEP', causes: ['Faulty RAM', 'Improperly seated RAM', 'Incompatible RAM', 'Failed memory slot'], solutions: ['Remove and reseat all RAM modules', 'Test RAM sticks one at a time', 'Try RAM in different slots', 'Run MemTest86', 'Replace faulty RAM'] },
    { code: '4 short beeps', name: 'Timer Failure', description: 'System timer not operational.', pattern: 'BEEP BEEP BEEP BEEP', causes: ['Motherboard timer chip failure', 'Motherboard defect'], solutions: ['Reset CMOS', 'Update BIOS', 'Test with minimal hardware', 'Likely motherboard replacement needed'] },
    { code: '5 short beeps', name: 'Processor Error', description: 'CPU failure detected.', pattern: 'BEEP BEEP BEEP BEEP BEEP', causes: ['CPU failure', 'CPU not properly seated', 'Bent CPU pins', 'Socket failure'], solutions: ['Check CPU is seated properly', 'Inspect for bent pins (if applicable)', 'Check CPU power connection', 'Test with known good CPU', 'May need CPU or motherboard replacement'] },
    { code: '6 short beeps', name: 'Keyboard Controller Error', description: 'Gate A20 failure - keyboard controller issue.', pattern: 'BEEP BEEP BEEP BEEP BEEP BEEP', causes: ['Keyboard controller chip failure', 'Motherboard issue'], solutions: ['Try different keyboard', 'Reset CMOS', 'Update BIOS', 'Likely motherboard replacement needed'] },
    { code: 'Continuous beeping', name: 'Memory or Video Failure', description: 'Continuous beeping indicates serious hardware failure.', pattern: 'BEEP BEEP BEEP BEEP BEEP...', causes: ['RAM not detected', 'Graphics card failure', 'Loose components'], solutions: ['Remove and reseat ALL RAM', 'Remove and reseat graphics card', 'Test with single RAM stick', 'Check all power connections', 'Test components individually'] },
  ],
  windows: [
    { code: 'Error 0x80070002', name: 'File Not Found', description: 'Windows cannot find specified file, often during updates.', causes: ['Corrupt update files', 'Missing system files', 'Third-party software interference'], solutions: ['Run Windows Update Troubleshooter', 'Run SFC: sfc /scannow', 'Run DISM: DISM /Online /Cleanup-Image /RestoreHealth', 'Clear Windows Update cache', 'Temporarily disable antivirus'] },
    { code: 'Error 0x80070057', name: 'Invalid Parameter', description: 'Parameter is incorrect, often seen during backups or Windows Updates.', causes: ['Corrupt registry', 'Incorrect Windows Update settings', 'Disk errors'], solutions: ['Run chkdsk /f /r', 'Reset Windows Update components', 'Check decimal symbol in regional settings (should be a period)', 'Create new backup destination', 'Run DISM scan'] },
    { code: 'Error 0x80240034', name: 'Windows Update Error', description: 'Windows Update encountered an unknown error.', causes: ['Corrupt update cache', 'Windows Update service issues', 'Network problems'], solutions: ['Restart Windows Update service', 'Clear update cache: stop wuauserv, delete C:\\Windows\\SoftwareDistribution, restart service', 'Run Windows Update Troubleshooter', 'Manually install updates', 'Check internet connection'] },
    { code: 'Error 0xc000000f', name: 'Boot Configuration Data Missing', description: 'Windows cannot access boot configuration.', causes: ['Corrupt BCD', 'Missing boot files', 'Incorrect BIOS boot order'], solutions: ['Use bootrec /fixmbr, bootrec /fixboot, bootrec /rebuildbcd', 'Use bcdboot c:\\windows /s c:', 'Check BIOS boot order', 'Repair Windows installation', 'Use System Restore'] },
    { code: 'Error 0xc0000225', name: 'Boot Selection Failed', description: 'Boot configuration data is missing or corrupt.', causes: ['Failed Windows update', 'Corrupt system files', 'Hard drive failure'], solutions: ['Boot to recovery and run Startup Repair', 'Rebuild BCD: bootrec /rebuildbcd', 'Run chkdsk from recovery', 'Check drive health', 'Use System Restore point'] },
  ],
  hardware: [
    { code: 'No Power / No POST', name: 'System Will Not Power On', description: 'Computer shows no signs of power when pressing power button.', causes: ['Dead PSU', 'Motherboard failure', 'Loose power connections', 'Failed power button'], solutions: ['Check all power cables firmly connected', 'Test PSU with paperclip test', 'Try different power outlet', 'Check power button connection to motherboard', 'Test with different PSU', 'Check motherboard standoffs properly installed'] },
    { code: 'Fans Spin, No Display', name: 'Power On But No Display', description: 'System powers on, fans spin, but no display output.', causes: ['Loose RAM', 'Graphics card not seated', 'Monitor cable issue', 'Dead monitor'], solutions: ['Reseat RAM firmly', 'Reseat graphics card', 'Try different monitor cable', 'Test with different monitor', 'Clear CMOS', 'Try onboard graphics (remove GPU)'] },
    { code: 'SMART Failure Predicted', name: 'Hard Drive Failure Warning', description: 'SMART monitoring detected imminent drive failure.', causes: ['Failing hard drive', 'Bad sectors', 'Mechanical failure', 'Firmware issues'], solutions: ['BACKUP DATA IMMEDIATELY', 'Run CrystalDiskInfo to check SMART values', 'Do NOT run chkdsk on failing drive', 'Clone drive to new drive ASAP', 'Replace drive', 'May need professional data recovery'] },
    { code: 'Boot Device Not Found', name: 'Operating System Not Found', description: 'BIOS cannot find bootable device.', causes: ['Disconnected drive cable', 'Failed hard drive', 'Incorrect BIOS boot order', 'Corrupt boot sector'], solutions: ['Check SATA cable connections', 'Verify boot order in BIOS', 'Check if drive is detected in BIOS', 'Try different SATA port', 'Test drive in different computer', 'Rebuild boot sector if drive is good'] },
    { code: 'CPU Fan Error', name: 'CPU Fan Not Detected', description: 'BIOS cannot detect CPU fan speed or fan has stopped.', causes: ['Dead CPU fan', 'Loose fan connection', 'Faulty fan header', 'BIOS settings'], solutions: ['Check CPU fan power cable firmly connected', 'Verify fan is spinning', 'Try fan in different header', 'Replace CPU fan if dead', 'Adjust fan monitoring in BIOS (if fan is working)', 'Clean fan and heatsink'] },
  ],
};

// ---------------------------------------------------------------------------
// Converted tool dataset: Driver Issue Resolver.
// Vendor and device identification tables and neutral driver guidance.
// External links are reference only. No automatic installation, download
// bundling, or launch behaviour.
// ---------------------------------------------------------------------------
export const DRIVER_VENDORS = {
  '8086': 'Intel',
  '10DE': 'NVIDIA',
  '1002': 'AMD/ATI',
  '14E4': 'Broadcom',
  '10EC': 'Realtek',
  '1969': 'Qualcomm Atheros',
  '168C': 'Qualcomm Atheros',
  '1022': 'AMD',
};

export const DRIVER_DEVICES = {
  '10DE': { '2204': 'NVIDIA GeForce RTX 3090', '2206': 'NVIDIA GeForce RTX 3080', '2484': 'NVIDIA GeForce RTX 3070', '2486': 'NVIDIA GeForce RTX 3060 Ti', '2544': 'NVIDIA GeForce RTX 3060' },
  '8086': { '7A82': 'Intel I219-V Ethernet', '15F3': 'Intel Ethernet I225-V', '2723': 'Intel Wi-Fi 6 AX200', '43F0': 'Intel Meteor Lake' },
  '10EC': { '8168': 'Realtek RTL8111/8168/8411 Gigabit Ethernet', 'B822': 'Realtek RTL8822BE 802.11ac WiFi', '0899': 'Realtek ALC899 Audio' },
  '1002': { '73BF': 'AMD Radeon RX 6900 XT', '73DF': 'AMD Radeon RX 6750 XT', '744C': 'AMD Radeon RX 7900 XTX' },
};

// Per-vendor reference links and installation guidance. Neutral wording.
export const DRIVER_VENDOR_GUIDANCE = {
  '10DE': {
    links: [{ label: 'NVIDIA Driver Download', url: 'https://www.nvidia.com/Download/index.aspx' }],
    note: 'GeForce Experience can automatically detect the correct driver.',
    steps: ['Download Display Driver Uninstaller (DDU)', 'Boot to Safe Mode', 'Run DDU, select NVIDIA, Clean and Restart', 'Install the latest driver from the NVIDIA website', 'Select Custom Installation and check Clean Install'],
  },
  '1002': {
    links: [{ label: 'AMD Driver Download', url: 'https://www.amd.com/en/support' }],
    note: 'AMD Software Adrenalin Edition can auto-detect the hardware.',
    steps: ['Download Display Driver Uninstaller (DDU)', 'Boot to Safe Mode', 'Run DDU, select AMD, Clean and Restart', 'Install AMD Adrenalin Software from the AMD website', 'Use the Factory Reset option during installation'],
  },
  '8086': {
    links: [{ label: 'Intel Download Center', url: 'https://downloadcenter.intel.com/' }],
    note: 'Intel Driver and Support Assistant can auto-detect drivers.',
    steps: ['Download Intel Driver and Support Assistant', 'Run a scan to detect outdated drivers', 'Install recommended updates', 'Restart when prompted'],
  },
  '10EC': {
    links: [{ label: 'Realtek Download', url: 'https://www.realtek.com/en/downloads' }],
    note: 'Check the motherboard manufacturer website for validated drivers.',
    steps: ['Device Manager then Network adapters', 'Right-click the device then Uninstall device', 'Check Delete driver software', 'Restart computer', 'Install a new driver from Realtek or the motherboard manufacturer'],
  },
};

// Generic guidance used when the vendor has no specific entry above.
export const DRIVER_GENERIC_GUIDANCE = {
  note: 'Check the motherboard or PC manufacturer website for validated drivers.',
  steps: ['Visit the manufacturer website', 'Navigate to the Support or Downloads section', 'Enter your model number', 'Download the latest driver for your Windows version', 'Run the installer and restart'],
};

// Always-shown safety notes for driver work. Neutral wording.
export const DRIVER_IMPORTANT_NOTES = [
  'Always download drivers from official manufacturer websites.',
  'Create a system restore point before installing drivers.',
  'If problems occur, disconnect from the internet during driver installation.',
  'Check Device Manager after installation to verify there are no errors.',
  'For network adapters, save the new driver to local storage before uninstalling the old driver.',
];

// Parse a Hardware ID string. Returns null when VEN/DEV cannot be extracted.
export function parseHardwareId(raw) {
  const input = (raw || '').toUpperCase();
  const venMatch = input.match(/VEN_([0-9A-F]{4})/);
  const devMatch = input.match(/DEV_([0-9A-F]{4})/);
  if (!venMatch || !devMatch) return null;
  const vendorID = venMatch[1];
  const deviceID = devMatch[1];
  const vendorName = DRIVER_VENDORS[vendorID] || ('Unknown Vendor (' + vendorID + ')');
  const deviceTable = DRIVER_DEVICES[vendorID];
  const deviceName = (deviceTable && deviceTable[deviceID]) || ('Device ' + deviceID);
  const guidance = DRIVER_VENDOR_GUIDANCE[vendorID] || null;
  return {
    input: input,
    vendorID: vendorID,
    deviceID: deviceID,
    vendorName: vendorName,
    deviceName: deviceName,
    guidance: guidance,
  };
}

// ---------------------------------------------------------------------------
// Converted tool: Hardware Compatibility Checker.
// Form option lists, compatibility tables, and a pure check engine. The engine
// is data-in / data-out and performs no product purchasing, quoting, supplier
// lookup, or sales behaviour.
// ---------------------------------------------------------------------------
export const HC_SOCKETS = [
  { value: 'AM4', label: 'AMD AM4' },
  { value: 'AM5', label: 'AMD AM5' },
  { value: 'LGA1700', label: 'Intel LGA1700 (12th/13th/14th Gen)' },
  { value: 'LGA1200', label: 'Intel LGA1200 (10th/11th Gen)' },
  { value: 'LGA1151', label: 'Intel LGA1151 (6th-9th Gen)' },
];

export const HC_CHIPSET_GROUPS = [
  { group: 'AMD', options: ['X670E', 'X670', 'B650E', 'B650', 'X570', 'B550', 'B450'] },
  { group: 'Intel', options: ['Z790', 'Z690', 'B760', 'B660', 'H770', 'H670', 'Z590', 'B560'] },
];

export const HC_RAM_TYPES = ['DDR5', 'DDR4', 'DDR3'];

export const HC_GPUS = [
  { value: 'RTX4090', label: 'RTX 4090' },
  { value: 'RTX4080', label: 'RTX 4080' },
  { value: 'RTX4070', label: 'RTX 4070' },
  { value: 'RTX4060', label: 'RTX 4060' },
  { value: 'RTX3090', label: 'RTX 3090' },
  { value: 'RTX3080', label: 'RTX 3080' },
  { value: 'RTX3070', label: 'RTX 3070' },
  { value: 'RTX3060', label: 'RTX 3060' },
  { value: 'RX7900XTX', label: 'RX 7900 XTX' },
  { value: 'RX7900XT', label: 'RX 7900 XT' },
  { value: 'RX7800XT', label: 'RX 7800 XT' },
  { value: 'RX7700XT', label: 'RX 7700 XT' },
  { value: 'RX6800XT', label: 'RX 6800 XT' },
  { value: 'RX6700XT', label: 'RX 6700 XT' },
];

export const HC_SOCKET_COMPAT = {
  AM4: { chipsets: ['X570', 'B550', 'B450'], ram: 'DDR4', ramSpeeds: [2133, 2400, 2666, 2933, 3200, 3600, 3733, 3866, 4000] },
  AM5: { chipsets: ['X670E', 'X670', 'B650E', 'B650'], ram: 'DDR5', ramSpeeds: [4800, 5200, 5600, 6000, 6400] },
  LGA1700: { chipsets: ['Z790', 'Z690', 'B760', 'B660', 'H770', 'H670'], ram: ['DDR4', 'DDR5'], ramSpeeds: { DDR4: [2133, 2400, 2666, 2933, 3200], DDR5: [4800, 5200, 5600, 6000, 6400] } },
  LGA1200: { chipsets: ['Z590', 'B560', 'H570'], ram: 'DDR4', ramSpeeds: [2133, 2400, 2666, 2933, 3200] },
  LGA1151: { chipsets: ['Z390', 'Z370', 'B360', 'H310'], ram: 'DDR4', ramSpeeds: [2133, 2400, 2666] },
};

export const HC_GPU_POWER = {
  RTX4090: 450, RTX4080: 320, RTX4070: 200, RTX4060: 115,
  RTX3090: 350, RTX3080: 320, RTX3070: 220, RTX3060: 170,
  RX7900XTX: 355, RX7900XT: 300, RX7800XT: 263, RX7700XT: 245,
  RX6800XT: 300, RX6700XT: 230,
};

// Pure engine. Returns { missing } when a field is blank, otherwise
// { allCompatible, rows } where each row is { kind, title, lines }.
export function checkCompatibility(inp) {
  const socket = inp.socket;
  const chipset = inp.chipset;
  const ramType = inp.ramType;
  const ramSpeed = parseInt(inp.ramSpeed, 10);
  const gpu = inp.gpu;
  const psu = parseInt(inp.psu, 10);

  if (!socket || !chipset || !ramType || !ramSpeed || !gpu || !psu) {
    return { missing: true };
  }

  const rows = [];
  let allCompatible = true;
  const data = HC_SOCKET_COMPAT[socket];

  // Socket / chipset
  if (data.chipsets.indexOf(chipset) !== -1) {
    rows.push({ kind: 'compatible', title: 'CPU Socket and Motherboard Compatible', lines: [socket + ' socket is compatible with ' + chipset + ' chipset'] });
  } else {
    allCompatible = false;
    rows.push({ kind: 'incompatible', title: 'CPU Socket and Motherboard Incompatible', lines: [socket + ' socket is not compatible with ' + chipset + ' chipset', 'Compatible chipsets for ' + socket + ': ' + data.chipsets.join(', ')] });
  }

  // RAM type
  if (typeof data.ram === 'string') {
    if (data.ram === ramType) {
      rows.push({ kind: 'compatible', title: 'RAM Type Compatible', lines: [ramType + ' is compatible with ' + socket] });
    } else {
      allCompatible = false;
      rows.push({ kind: 'incompatible', title: 'RAM Type Incompatible', lines: [ramType + ' is not compatible with ' + socket, 'Required RAM type: ' + data.ram] });
    }
  } else {
    if (data.ram.indexOf(ramType) !== -1) {
      rows.push({ kind: 'compatible', title: 'RAM Type Compatible', lines: [ramType + ' is compatible with ' + socket] });
    } else {
      allCompatible = false;
      rows.push({ kind: 'incompatible', title: 'RAM Type Incompatible', lines: [ramType + ' is not compatible with ' + socket, 'Supported RAM types: ' + data.ram.join(', ')] });
    }
  }

  // RAM speed
  if (typeof data.ramSpeeds === 'object' && !Array.isArray(data.ramSpeeds)) {
    const speeds = data.ramSpeeds[ramType] || [];
    const maxSpeed = Math.max.apply(null, speeds);
    const speedSupported = ramSpeed <= maxSpeed;
    if (speedSupported) {
      if (ramSpeed > speeds[speeds.length - 1]) {
        rows.push({ kind: 'warning', title: 'RAM Speed May Need XMP/DOCP', lines: [ramSpeed + 'MHz RAM may require enabling XMP/DOCP in BIOS', 'JEDEC speeds: ' + speeds.slice(0, 3).join(', ') + 'MHz (no config needed)', 'Max supported: ' + maxSpeed + 'MHz+ with XMP/DOCP'] });
      } else {
        rows.push({ kind: 'compatible', title: 'RAM Speed Compatible', lines: [ramSpeed + 'MHz is supported (max: ' + maxSpeed + 'MHz+)'] });
      }
    } else {
      rows.push({ kind: 'warning', title: 'RAM Speed May Not Reach Full Spec', lines: [ramSpeed + 'MHz may not be achievable', 'Recommended max: ' + maxSpeed + 'MHz'] });
    }
  } else {
    const speeds = data.ramSpeeds;
    const maxSpeed = Math.max.apply(null, speeds);
    if (ramSpeed <= maxSpeed * 1.2) {
      rows.push({ kind: 'compatible', title: 'RAM Speed Compatible', lines: [ramSpeed + 'MHz is within supported range (up to about ' + Math.floor(maxSpeed * 1.2) + 'MHz)'] });
    } else {
      rows.push({ kind: 'warning', title: 'RAM Speed Very High', lines: [ramSpeed + 'MHz is beyond typical limits', 'Recommended max: ' + maxSpeed + 'MHz'] });
    }
  }

  // PSU wattage
  const gpuWattage = HC_GPU_POWER[gpu];
  const cpuWattage = 150;
  const systemWattage = 100;
  const totalNeeded = gpuWattage + cpuWattage + systemWattage;
  const recommended = Math.ceil(totalNeeded * 1.2 / 50) * 50;

  if (psu >= recommended) {
    rows.push({ kind: 'compatible', title: 'PSU Wattage Sufficient', lines: [psu + 'W PSU is adequate for this system', 'Estimated power draw: ' + totalNeeded + 'W', 'Recommended PSU: ' + recommended + 'W+'] });
  } else if (psu >= totalNeeded) {
    rows.push({ kind: 'warning', title: 'PSU Wattage Marginal', lines: [psu + 'W PSU will work but has minimal headroom', 'Estimated power draw: ' + totalNeeded + 'W', 'Recommended PSU: ' + recommended + 'W+ for safety'] });
  } else {
    allCompatible = false;
    rows.push({ kind: 'incompatible', title: 'PSU Wattage Insufficient', lines: [psu + 'W PSU is not enough for this system', 'Estimated power draw: ' + totalNeeded + 'W', 'Required PSU: ' + recommended + 'W minimum'] });
  }

  return { allCompatible: allCompatible, rows: rows };
}

// ---------------------------------------------------------------------------
// Converted tool: System Bottleneck Analyzer.
// Form option lists, CPU and GPU score tables, and a pure analysis engine.
// Technical analysis only. No sales quote, supplier links, or pricing.
// ---------------------------------------------------------------------------
export const BN_CPUS = [
  { value: 'i9-14900K', label: 'Intel i9-14900K' },
  { value: 'i7-14700K', label: 'Intel i7-14700K' },
  { value: 'i5-14600K', label: 'Intel i5-14600K' },
  { value: 'i5-12400F', label: 'Intel i5-12400F' },
  { value: 'i3-12100F', label: 'Intel i3-12100F' },
  { value: 'Ryzen9-7950X', label: 'AMD Ryzen 9 7950X' },
  { value: 'Ryzen7-7800X3D', label: 'AMD Ryzen 7 7800X3D' },
  { value: 'Ryzen5-7600X', label: 'AMD Ryzen 5 7600X' },
  { value: 'Ryzen5-5600X', label: 'AMD Ryzen 5 5600X' },
  { value: 'Ryzen3-3300X', label: 'AMD Ryzen 3 3300X' },
  { value: 'i7-8700K', label: 'Intel i7-8700K (older)' },
  { value: 'i5-7400', label: 'Intel i5-7400 (older)' },
];

export const BN_GPUS = [
  { value: 'RTX4090', label: 'RTX 4090' },
  { value: 'RTX4080', label: 'RTX 4080' },
  { value: 'RTX4070', label: 'RTX 4070' },
  { value: 'RTX4060', label: 'RTX 4060' },
  { value: 'RTX3090', label: 'RTX 3090' },
  { value: 'RTX3080', label: 'RTX 3080' },
  { value: 'RTX3070', label: 'RTX 3070' },
  { value: 'RTX3060', label: 'RTX 3060' },
  { value: 'RX7900XTX', label: 'RX 7900 XTX' },
  { value: 'RX7800XT', label: 'RX 7800 XT' },
  { value: 'RX6800XT', label: 'RX 6800 XT' },
  { value: 'RX6700XT', label: 'RX 6700 XT' },
  { value: 'GTX1660', label: 'GTX 1660 (older)' },
  { value: 'GTX1060', label: 'GTX 1060 (older)' },
];

export const BN_RAM = [
  { value: '8', label: '8GB' },
  { value: '16', label: '16GB' },
  { value: '32', label: '32GB' },
  { value: '64', label: '64GB' },
];

export const BN_STORAGE = [
  { value: 'NVME', label: 'NVMe SSD' },
  { value: 'SATA_SSD', label: 'SATA SSD' },
  { value: 'HDD', label: 'HDD (Mechanical)' },
];

export const BN_USECASES = [
  { value: 'gaming', label: 'Gaming' },
  { value: 'video', label: 'Video Editing' },
  { value: '3d', label: '3D Rendering' },
  { value: 'office', label: 'Office / General' },
  { value: 'streaming', label: 'Game Streaming' },
];

export const BN_CPU_SCORES = {
  'i9-14900K': 95, 'i7-14700K': 85, 'i5-14600K': 75, 'i5-12400F': 65, 'i3-12100F': 50,
  'Ryzen9-7950X': 100, 'Ryzen7-7800X3D': 90, 'Ryzen5-7600X': 70, 'Ryzen5-5600X': 60, 'Ryzen3-3300X': 45,
  'i7-8700K': 55, 'i5-7400': 40,
};

export const BN_GPU_SCORES = {
  RTX4090: 100, RTX4080: 90, RTX4070: 75, RTX4060: 60,
  RTX3090: 85, RTX3080: 80, RTX3070: 70, RTX3060: 55,
  RX7900XTX: 95, RX7800XT: 72, RX6800XT: 78, RX6700XT: 65,
  GTX1660: 40, GTX1060: 30,
};

function bnLabel(list, value) {
  for (let i = 0; i < list.length; i++) {
    if (list[i].value === value) return list[i].label;
  }
  return value;
}

// Pure engine. Returns { missing } when a field is blank, otherwise a full
// structured analysis result.
export function analyzeBottleneck(inp) {
  const cpu = inp.cpu;
  const gpu = inp.gpu;
  const ram = parseInt(inp.ram, 10);
  const storage = inp.storage;
  const useCase = inp.useCase;

  if (!cpu || !gpu || !ram || !storage || !useCase) {
    return { missing: true };
  }

  const cpuScore = BN_CPU_SCORES[cpu];
  const gpuScore = BN_GPU_SCORES[gpu];
  const gap = Math.abs(cpuScore - gpuScore);
  const cpuLabel = bnLabel(BN_CPUS, cpu);
  const gpuLabel = bnLabel(BN_GPUS, gpu);

  let bottleneck = '';
  let severity = '';
  const recommendations = [];

  if (gap > 25) {
    if (cpuScore > gpuScore) {
      bottleneck = 'GPU';
      severity = 'Severe';
      recommendations.push('Your ' + cpuLabel + ' is significantly more powerful than your ' + gpuLabel);
      recommendations.push('GPU upgrade will provide a large performance boost');
      recommendations.push('Recommended GPU upgrade: ' + (cpuScore > 80 ? 'RTX 4080/4090 or RX 7900 XTX' : 'RTX 4070 or RX 7800 XT'));
    } else {
      bottleneck = 'CPU';
      severity = 'Severe';
      recommendations.push('Your ' + gpuLabel + ' is being held back by your ' + cpuLabel);
      recommendations.push('CPU upgrade will unlock GPU potential');
      recommendations.push('Recommended CPU upgrade: ' + (gpuScore > 80 ? 'i7-14700K / Ryzen 7 7800X3D' : 'i5-14600K / Ryzen 5 7600X'));
    }
  } else if (gap > 15) {
    if (cpuScore > gpuScore) {
      bottleneck = 'GPU';
      severity = 'Moderate';
      recommendations.push('GPU is limiting performance in graphics-intensive tasks');
      recommendations.push('Consider a GPU upgrade to RTX 4070 or better');
    } else {
      bottleneck = 'CPU';
      severity = 'Moderate';
      recommendations.push('CPU limiting performance in multi-threaded workloads');
      recommendations.push('Consider a CPU upgrade to i5-14600K or Ryzen 5 7600X or better');
    }
  } else {
    bottleneck = 'None';
    severity = 'Balanced';
    recommendations.push('CPU and GPU are well balanced for each other');
    recommendations.push('Focus on other upgrades if needed');
  }

  let ramBottleneck = false;
  if (useCase === 'gaming' && ram < 16) {
    ramBottleneck = true;
    recommendations.push('\u26A0 16GB RAM minimum for modern gaming - upgrade to 16GB');
  } else if (useCase === 'video' && ram < 32) {
    ramBottleneck = true;
    recommendations.push('\u26A0 Video editing benefits from 32GB or more RAM');
  } else if (useCase === '3d' && ram < 32) {
    ramBottleneck = true;
    recommendations.push('\u26A0 3D rendering needs 32GB or more RAM for best performance');
  } else if (useCase === 'streaming' && ram < 16) {
    ramBottleneck = true;
    recommendations.push('\u26A0 Game streaming while playing needs 16GB or more RAM');
  }

  let storageBottleneck = false;
  if (storage === 'HDD') {
    storageBottleneck = true;
    recommendations.push('\u{1F6A8} Critical: HDD is a major bottleneck - upgrade to NVMe SSD as a priority');
    recommendations.push('NVMe SSD will improve boot times, game loading, and overall system responsiveness');
  } else if (storage === 'SATA_SSD') {
    recommendations.push('\u{1F4A1} Consider an NVMe SSD upgrade for faster storage speeds');
  }

  const topUpgrade = storageBottleneck ? 'Storage' : (ramBottleneck ? 'RAM' : bottleneck);
  const estimatedGain = storageBottleneck ? '300-500%' : (ramBottleneck ? '50-100%' : (severity === 'Severe' ? '50-80%' : '20-40%'));

  return {
    cpu: cpu, gpu: gpu, cpuLabel: cpuLabel, gpuLabel: gpuLabel,
    cpuScore: cpuScore, gpuScore: gpuScore,
    bottleneck: bottleneck, severity: severity,
    recommendations: recommendations,
    ram: ram, ramBottleneck: ramBottleneck,
    storage: storage, storageLabel: bnLabel(BN_STORAGE, storage), storageBottleneck: storageBottleneck,
    topUpgrade: topUpgrade, estimatedGain: estimatedGain,
  };
}

// ---------------------------------------------------------------------------
// Converted tool: PC Build Configurator.
//
// Technician build-planning only. The source was a branded customer-quote
// generator; the customer record, branded quote header, service-inclusion
// list, shop footer, and "final quote" language are intentionally not carried
// across. This tool plans a build, shows component prices, and computes a
// planning total. It has no ordering, cart, invoice, customer record, or
// persistence.
//
// Supplier CSV import is a manual, technician-initiated, in-session feature:
// the technician selects a CSV file through the browser file picker, it is
// parsed in memory for the current session only, and the parsed products
// become selectable build parts. There is no folder scanning, no hardcoded
// supplier path, and no assumption of any single supplier.
// ---------------------------------------------------------------------------
export const BUILD_COMPONENTS = [
  { key: 'cpu',         label: 'Processor (CPU)' },
  { key: 'motherboard', label: 'Motherboard' },
  { key: 'ram',         label: 'Memory (RAM)' },
  { key: 'gpu',         label: 'Graphics Card (GPU)' },
  { key: 'storage',     label: 'Storage Drive' },
  { key: 'psu',         label: 'Power Supply (PSU)' },
  { key: 'pcCase',      label: 'Case' },
  { key: 'cooler',      label: 'CPU Cooler' },
];

export const BUILD_TYPES = [
  { value: 'budget_gaming', label: 'Budget Gaming' },
  { value: 'mid_gaming',    label: 'Mid-Range Gaming' },
  { value: 'high_gaming',   label: 'High-End Gaming' },
  { value: 'workstation',   label: 'Professional Workstation' },
  { value: 'office',        label: 'Office / General Use' },
  { value: 'custom',        label: 'Custom (Manual Entry)' },
];

// Reference component suggestions per build type. Names and indicative prices
// are planning references only.
export const BUILD_PRESETS = {
  budget_gaming: {
    cpu: { name: 'AMD Ryzen 5 5600', price: 199 },
    motherboard: { name: 'MSI B550M PRO-VDH WIFI', price: 169 },
    ram: { name: 'Corsair Vengeance 16GB DDR4-3200', price: 89 },
    gpu: { name: 'AMD RX 6700 XT 12GB', price: 449 },
    storage: { name: 'WD Black SN770 500GB NVMe', price: 79 },
    psu: { name: 'Corsair CV650 650W 80+ Bronze', price: 89 },
    pcCase: { name: 'Cooler Master Q300L', price: 69 },
    cooler: { name: 'Deepcool GAMMAXX 400', price: 39 },
  },
  mid_gaming: {
    cpu: { name: 'AMD Ryzen 7 7700X', price: 359 },
    motherboard: { name: 'MSI B650 GAMING PLUS WIFI', price: 259 },
    ram: { name: 'G.Skill Flare X5 32GB DDR5-6000', price: 189 },
    gpu: { name: 'NVIDIA RTX 4070 12GB', price: 799 },
    storage: { name: 'Samsung 990 Pro 1TB NVMe', price: 159 },
    psu: { name: 'Corsair RM750e 750W 80+ Gold', price: 139 },
    pcCase: { name: 'Fractal Design Meshify 2', price: 149 },
    cooler: { name: 'Deepcool AK620', price: 79 },
  },
  high_gaming: {
    cpu: { name: 'AMD Ryzen 9 7950X3D', price: 699 },
    motherboard: { name: 'ASUS ROG STRIX X670E-E', price: 499 },
    ram: { name: 'G.Skill Trident Z5 RGB 32GB DDR5-6400', price: 269 },
    gpu: { name: 'NVIDIA RTX 4090 24GB', price: 2499 },
    storage: { name: 'Samsung 990 Pro 2TB NVMe', price: 279 },
    psu: { name: 'Corsair HX1000i 1000W 80+ Platinum', price: 299 },
    pcCase: { name: 'Lian Li O11 Dynamic EVO', price: 199 },
    cooler: { name: 'NZXT Kraken X73 360mm AIO', price: 199 },
  },
  workstation: {
    cpu: { name: 'AMD Ryzen 9 7950X', price: 599 },
    motherboard: { name: 'ASUS ProArt X670E-CREATOR', price: 549 },
    ram: { name: 'G.Skill Flare X5 64GB DDR5-5600', price: 349 },
    gpu: { name: 'NVIDIA RTX 4070 Ti 12GB', price: 999 },
    storage: { name: 'Samsung 990 Pro 2TB NVMe', price: 279 },
    psu: { name: 'Corsair RM850x 850W 80+ Gold', price: 179 },
    pcCase: { name: 'Fractal Design Define 7', price: 189 },
    cooler: { name: 'Noctua NH-D15', price: 119 },
  },
  office: {
    cpu: { name: 'Intel i5-12400', price: 219 },
    motherboard: { name: 'MSI B660M-A PRO', price: 149 },
    ram: { name: 'Corsair Vengeance 16GB DDR4-3200', price: 89 },
    gpu: { name: 'Integrated Graphics', price: 0 },
    storage: { name: 'WD Blue SN570 500GB NVMe', price: 69 },
    psu: { name: 'Corsair CV450 450W 80+ Bronze', price: 59 },
    pcCase: { name: 'Cooler Master Q300L', price: 69 },
    cooler: { name: 'Intel Stock Cooler', price: 0 },
  },
};

// Minimal, dependency-free CSV parser. Handles quoted fields and commas within
// quotes. Returns an array of row objects keyed by header name (lowercased).
export function parseCsv(text) {
  const rows = [];
  const lines = (text || '').split(/\r\n|\n|\r/);
  let header = null;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (raw === '' || raw === null) continue;
    const cells = [];
    let cur = '';
    let inQuotes = false;
    for (let c = 0; c < raw.length; c++) {
      const ch = raw[c];
      if (ch === '"') {
        if (inQuotes && raw[c + 1] === '"') { cur += '"'; c++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        cells.push(cur); cur = '';
      } else {
        cur += ch;
      }
    }
    cells.push(cur);
    if (!header) {
      header = cells.map(h => h.trim().toLowerCase());
      continue;
    }
    const obj = {};
    for (let h = 0; h < header.length; h++) {
      obj[header[h]] = (cells[h] !== undefined ? cells[h] : '').trim();
    }
    rows.push(obj);
  }
  return rows;
}

// Supplier-neutral mapping. Detects a name/description column, a price/cost
// column, and an optional category/component column from arbitrary CSV
// headers. Returns { products, columns } where each product is
// { name, price, category }.
export function parseSupplierCsv(text) {
  const rows = parseCsv(text);
  if (rows.length === 0) return { products: [], detected: null };
  const keys = Object.keys(rows[0]);
  const findKey = candidates => {
    for (let i = 0; i < keys.length; i++) {
      for (let j = 0; j < candidates.length; j++) {
        if (keys[i].indexOf(candidates[j]) !== -1) return keys[i];
      }
    }
    return null;
  };
  const nameKey = findKey(['name', 'description', 'product', 'item', 'model', 'title']);
  const priceKey = findKey(['price', 'cost', 'rrp', 'amount', 'sell', 'ex gst', 'inc gst']);
  const catKey = findKey(['category', 'type', 'component', 'group', 'class']);

  const products = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const name = nameKey ? r[nameKey] : '';
    if (!name) continue;
    let price = 0;
    if (priceKey && r[priceKey]) {
      const cleaned = r[priceKey].replace(/[^0-9.\-]/g, '');
      const parsed = parseFloat(cleaned);
      price = isNaN(parsed) ? 0 : parsed;
    }
    const category = catKey ? r[catKey] : '';
    products.push({ name: name, price: price, category: category });
  }
  return { products: products, detected: { nameKey: nameKey, priceKey: priceKey, catKey: catKey } };
}

// Pure totals engine. parts is a map of componentKey -> { name, price }.
export function computeBuildTotals(parts, assemblyFee, extras) {
  let partsTotal = 0;
  const keys = Object.keys(parts);
  for (let i = 0; i < keys.length; i++) {
    const p = parts[keys[i]];
    if (p && p.name) partsTotal += (parseFloat(p.price) || 0);
  }
  if (extras && extras.name) partsTotal += (parseFloat(extras.price) || 0);
  const fee = parseFloat(assemblyFee) || 0;
  return { partsTotal: partsTotal, assemblyFee: fee, total: partsTotal + fee };
}

// ---------------------------------------------------------------------------
// Converted tool: Gaming PC Calculator.
//
// Technical suitability guidance only. No sales quote, supplier path, ordering
// flow, or customer quote. The source's budget figure is retained as a neutral
// approximate hardware cost range for planning.
// ---------------------------------------------------------------------------
export const GAMING_GAMES = [
  { value: 'esports',     label: 'E-Sports (CS2, Valorant, LoL, Dota 2)' },
  { value: 'aaa',         label: 'AAA Titles (Cyberpunk, RDR2, Starfield)' },
  { value: 'competitive', label: 'Competitive FPS (CoD, Battlefield)' },
  { value: 'sim',         label: 'Simulation (Flight Sim, City Builders)' },
  { value: 'vr',          label: 'VR Gaming' },
];

export const GAMING_RESOLUTIONS = [
  { value: '1080p', label: '1080p (1920x1080)' },
  { value: '1440p', label: '1440p (2560x1440)' },
  { value: '4k',    label: '4K (3840x2160)' },
];

export const GAMING_FPS = [
  { value: '60',  label: '60 FPS (Smooth)' },
  { value: '120', label: '120 FPS (High Refresh)' },
  { value: '144', label: '144 FPS (Competitive)' },
  { value: '240', label: '240+ FPS (Pro)' },
];

export const GAMING_QUALITY = [
  { value: 'low',    label: 'Low (Max FPS)' },
  { value: 'medium', label: 'Medium (Balanced)' },
  { value: 'high',   label: 'High (Good Visuals)' },
  { value: 'ultra',  label: 'Ultra (Max Quality)' },
];

function gamingLabel(list, value) {
  for (let i = 0; i < list.length; i++) {
    if (list[i].value === value) return list[i].label;
  }
  return value;
}

// Pure engine. Returns { missing } when a field is blank, otherwise the full
// recommendation set. Logic transcribed faithfully from the source.
export function calculateGamingSpecs(inp) {
  const gameType = inp.gameType;
  const resolution = inp.resolution;
  const targetFPS = parseInt(inp.targetFPS, 10);
  const quality = inp.quality;

  if (!gameType || !resolution || !targetFPS || !quality) {
    return { missing: true };
  }

  const demandScore =
    (gameType === 'esports' ? 1 : gameType === 'competitive' ? 2 : gameType === 'sim' ? 3 : gameType === 'aaa' ? 4 : 5) +
    (resolution === '1080p' ? 1 : resolution === '1440p' ? 2 : 3) +
    (quality === 'low' ? 0 : quality === 'medium' ? 1 : quality === 'high' ? 2 : 3);

  let gpuRec, cpuRec, ramRec, storageRec, estimatedFPS;

  if (resolution === '4k') {
    if (quality === 'ultra') {
      gpuRec = 'RTX 4080 or RX 7900 XTX';
      estimatedFPS = targetFPS >= 120 ? '70-90 FPS' : '85-110 FPS';
    } else {
      gpuRec = 'RTX 4070 Ti or RX 7900 XT';
      estimatedFPS = targetFPS >= 120 ? '80-100 FPS' : '100-120 FPS';
    }
  } else if (resolution === '1440p') {
    if (demandScore >= 7) {
      gpuRec = 'RTX 4070 or RX 7800 XT';
      estimatedFPS = targetFPS >= 144 ? '110-140 FPS' : '140-180 FPS';
    } else {
      gpuRec = 'RTX 4060 Ti or RX 7700 XT';
      estimatedFPS = targetFPS >= 144 ? '120-150 FPS' : '160-200 FPS';
    }
  } else {
    if (targetFPS >= 240) {
      gpuRec = 'RTX 4070 or RX 7800 XT';
      estimatedFPS = '240-300+ FPS';
    } else if (demandScore >= 6) {
      gpuRec = 'RTX 4060 or RX 7600';
      estimatedFPS = '140-180 FPS';
    } else {
      gpuRec = 'RTX 3060 or RX 6700 XT';
      estimatedFPS = '100-144 FPS';
    }
  }

  if (targetFPS >= 240 || gameType === 'esports') {
    cpuRec = 'Intel i5-14600K or AMD Ryzen 7 7700X';
  } else if (gameType === 'sim' || resolution === '4k') {
    cpuRec = 'Intel i7-14700K or AMD Ryzen 7 7800X3D';
  } else {
    cpuRec = 'Intel i5-14400F or AMD Ryzen 5 7600';
  }

  if (gameType === 'aaa' || quality === 'ultra') {
    ramRec = '32GB DDR5-6000 (16GB minimum, 32GB recommended)';
  } else {
    ramRec = '16GB DDR4-3200 or DDR5-5600';
  }

  if (gameType === 'aaa' || gameType === 'sim') {
    storageRec = '1TB NVMe SSD (games 100-150GB each)';
  } else {
    storageRec = '500GB NVMe SSD';
  }

  const gpuCost = gpuRec.indexOf('4080') !== -1 ? 1200 :
    gpuRec.indexOf('4070 Ti') !== -1 ? 800 :
    gpuRec.indexOf('4070') !== -1 ? 700 :
    gpuRec.indexOf('4060 Ti') !== -1 ? 500 :
    gpuRec.indexOf('4060') !== -1 ? 400 : 350;
  const cpuCost = (cpuRec.indexOf('i7') !== -1 || cpuRec.indexOf('7800X3D') !== -1) ? 400 :
    (cpuRec.indexOf('i5-14600K') !== -1 || cpuRec.indexOf('7700X') !== -1) ? 350 : 250;
  const totalBudget = gpuCost + cpuCost + 400;
  const costLow = totalBudget;
  const costHigh = totalBudget + 300;

  const monitor = resolution === '4k' ? '4K 60Hz or 1440p 144Hz' : resolution === '1440p' ? '1440p 165Hz' : targetFPS >= 240 ? '1080p 240Hz+' : '1080p 144Hz';
  const psu = (gpuRec.indexOf('4080') !== -1 || gpuRec.indexOf('7900') !== -1) ? '850W 80+ Gold' : gpuRec.indexOf('4070') !== -1 ? '750W 80+ Gold' : '650W 80+ Bronze';
  const cooling = (cpuRec.indexOf('i7') !== -1 || cpuRec.indexOf('7800X3D') !== -1) ? 'AIO liquid cooler recommended' : 'Tower air cooler sufficient';
  const internet = (gameType === 'competitive' || gameType === 'esports') ? 'Wired Ethernet highly recommended' : 'WiFi acceptable';

  return {
    summaryFor: gamingLabel(GAMING_GAMES, gameType) + ' at ' + resolution + ' ' + targetFPS + '+ FPS (' + quality + ' quality)',
    gpuRec: gpuRec, cpuRec: cpuRec, ramRec: ramRec, storageRec: storageRec,
    estimatedFPS: estimatedFPS,
    costLow: costLow, costHigh: costHigh,
    monitor: monitor, psu: psu, cooling: cooling, internet: internet,
  };
}

// ---------------------------------------------------------------------------
// Converted tool: Workstation Builder.
//
// Technician workstation planning only. The source included a Customer Name
// "for quote generation" field and a branded quote header; those are not
// carried across. Per-tier cost is retained only as an approximate hardware
// planning estimate, never a quote. No ordering, cart, invoice, customer
// record, or persistence.
// ---------------------------------------------------------------------------
export const WS_WORKLOADS = [
  { value: 'cad',   label: 'CAD/Engineering (AutoCAD, SolidWorks, Revit)' },
  { value: 'video', label: 'Video Editing (Premiere Pro, DaVinci Resolve)' },
  { value: '3d',    label: '3D Rendering (Blender, Maya, 3ds Max)' },
  { value: 'data',  label: 'Data Science/ML (Python, TensorFlow, Large Datasets)' },
  { value: 'audio', label: 'Audio Production (Pro Tools, Ableton)' },
  { value: 'photo', label: 'Photography (Lightroom, Photoshop)' },
];

export const WS_BUDGETS = [
  { value: 'entry', label: 'Entry (approx 1500-2500)' },
  { value: 'mid',   label: 'Mid-Range (approx 2500-4000)' },
  { value: 'high',  label: 'High-End (approx 4000-6000)' },
  { value: 'pro',   label: 'Professional (approx 6000+)' },
];

export const WS_WORKLOAD_NAMES = {
  cad: 'CAD/Engineering Workstation',
  video: 'Video Editing Workstation',
  '3d': '3D Rendering Workstation',
  data: 'Data Science/ML Workstation',
  audio: 'Audio Production Workstation',
  photo: 'Photography Workstation',
};

export const WS_CONFIGS = {
  cad: {
    entry: { cpu: 'Intel i7-14700', ram: '32GB DDR5-4800', gpu: 'NVIDIA RTX 4060 Ti 16GB', storage: '1TB NVMe SSD', cost: 2200 },
    mid: { cpu: 'Intel i7-14700K', ram: '64GB DDR5-5600', gpu: 'NVIDIA RTX 4070 Ti 12GB', storage: '2TB NVMe SSD', cost: 3500 },
    high: { cpu: 'Intel i9-14900K', ram: '64GB DDR5-6000', gpu: 'NVIDIA RTX 4080 16GB', storage: '2TB NVMe + 4TB SATA SSD', cost: 5500 },
    pro: { cpu: 'AMD Threadripper 7960X', ram: '128GB DDR5-5600 ECC', gpu: 'NVIDIA RTX 4090 24GB', storage: '4TB NVMe + 8TB SATA SSD', cost: 8500 },
  },
  video: {
    entry: { cpu: 'AMD Ryzen 7 7700X', ram: '32GB DDR5-5600', gpu: 'NVIDIA RTX 4060 Ti 16GB', storage: '2TB NVMe SSD', cost: 2300 },
    mid: { cpu: 'AMD Ryzen 9 7900X', ram: '64GB DDR5-6000', gpu: 'NVIDIA RTX 4070 Ti 12GB', storage: '4TB NVMe + 4TB HDD', cost: 3800 },
    high: { cpu: 'AMD Ryzen 9 7950X', ram: '96GB DDR5-6000', gpu: 'NVIDIA RTX 4080 16GB', storage: '4TB NVMe + 12TB HDD', cost: 5800 },
    pro: { cpu: 'AMD Threadripper 7970X', ram: '128GB DDR5-5600', gpu: 'NVIDIA RTX 4090 24GB', storage: '8TB NVMe RAID + 24TB HDD', cost: 9500 },
  },
  '3d': {
    entry: { cpu: 'AMD Ryzen 9 7900X', ram: '64GB DDR5-5600', gpu: 'NVIDIA RTX 4070 12GB', storage: '2TB NVMe SSD', cost: 3000 },
    mid: { cpu: 'AMD Ryzen 9 7950X', ram: '96GB DDR5-6000', gpu: 'NVIDIA RTX 4080 16GB', storage: '4TB NVMe SSD', cost: 4800 },
    high: { cpu: 'AMD Threadripper 7960X', ram: '128GB DDR5-5600', gpu: 'NVIDIA RTX 4090 24GB', storage: '4TB NVMe + 8TB SATA', cost: 7500 },
    pro: { cpu: 'AMD Threadripper 7980X', ram: '256GB DDR5-5600 ECC', gpu: '2x NVIDIA RTX 4090 24GB', storage: '8TB NVMe RAID + 16TB', cost: 15000 },
  },
  data: {
    entry: { cpu: 'AMD Ryzen 9 7900X', ram: '64GB DDR5-5600', gpu: 'NVIDIA RTX 4060 Ti 16GB', storage: '2TB NVMe SSD', cost: 2800 },
    mid: { cpu: 'AMD Ryzen 9 7950X', ram: '128GB DDR5-5600', gpu: 'NVIDIA RTX 4070 Ti 12GB', storage: '4TB NVMe SSD', cost: 4200 },
    high: { cpu: 'AMD Threadripper 7960X', ram: '192GB DDR5-5600', gpu: 'NVIDIA RTX 4080 16GB', storage: '8TB NVMe + 16TB HDD', cost: 7800 },
    pro: { cpu: 'AMD Threadripper 7980X', ram: '512GB DDR5-5600 ECC', gpu: '2x NVIDIA RTX 4090 24GB', storage: '16TB NVMe RAID', cost: 18000 },
  },
  audio: {
    entry: { cpu: 'Intel i7-14700', ram: '32GB DDR5-4800', gpu: 'Integrated Graphics', storage: '1TB NVMe SSD', cost: 1600 },
    mid: { cpu: 'Intel i9-14900', ram: '64GB DDR5-5600', gpu: 'Basic GPU', storage: '2TB NVMe SSD', cost: 2800 },
    high: { cpu: 'Intel i9-14900K', ram: '96GB DDR5-6000', gpu: 'Basic GPU', storage: '4TB NVMe + 4TB SATA', cost: 4200 },
    pro: { cpu: 'AMD Threadripper 7960X', ram: '128GB DDR5-5600', gpu: 'Basic GPU', storage: '8TB NVMe RAID', cost: 7500 },
  },
  photo: {
    entry: { cpu: 'AMD Ryzen 7 7700X', ram: '32GB DDR5-5600', gpu: 'NVIDIA RTX 4060 8GB', storage: '2TB NVMe SSD', cost: 2000 },
    mid: { cpu: 'AMD Ryzen 9 7900X', ram: '64GB DDR5-6000', gpu: 'NVIDIA RTX 4070 12GB', storage: '4TB NVMe + 8TB HDD', cost: 3500 },
    high: { cpu: 'AMD Ryzen 9 7950X', ram: '96GB DDR5-6000', gpu: 'NVIDIA RTX 4070 Ti 12GB', storage: '8TB NVMe + 16TB HDD', cost: 5500 },
    pro: { cpu: 'AMD Threadripper 7970X', ram: '128GB DDR5-5600', gpu: 'NVIDIA RTX 4080 16GB', storage: '16TB NVMe + 32TB HDD', cost: 9000 },
  },
};

export const WS_WORKLOAD_NOTES = {
  cad: 'High single-thread CPU performance for CAD. Professional GPU with certified drivers. ECC RAM for critical work at Pro tier.',
  video: 'Multi-core CPU for encoding. Large fast storage for 4K/8K footage. GPU acceleration for effects and color grading.',
  '3d': 'Maximum CPU cores for rendering. High VRAM GPU. Multiple GPUs at Pro tier for GPU rendering engines.',
  data: 'High core count for parallel processing. Large RAM for datasets. CUDA GPU for TensorFlow/PyTorch.',
  audio: 'Focus on CPU single-thread performance. Minimal GPU needed. Fast storage for sample libraries.',
  photo: 'Balanced CPU/GPU. Large storage for RAW files. GPU for AI features and filters.',
};

export const WS_ADDITIONAL_RECS = {
  cad: ['Dual monitors (27 inch 1440p or 4K)', '3D mouse (SpaceMouse)', 'UPS for power protection'],
  video: ['Color-accurate monitor (99% Adobe RGB)', 'RAID storage for redundancy', '10GbE network for shared storage'],
  '3d': ['Dual monitors for viewport and timeline', 'Render farm for final renders', 'High-end cooling (AIO liquid)'],
  data: ['Large monitors for code and data', 'Fast network for dataset transfer', 'Linux dual-boot option'],
  audio: ['Audio interface (Focusrite, UAD)', 'Studio monitors', 'Quiet cooling (important)'],
  photo: ['Color calibration (X-Rite)', 'Backup strategy (3-2-1 rule)', 'Card readers for workflow'],
};

// Pure engine. Returns { missing } when workload or budget is blank, otherwise
// the selected configuration plus its notes and additional recommendations.
export function buildWorkstationSpec(inp) {
  const workload = inp.workload;
  const budget = inp.budget;
  if (!workload || !budget) return { missing: true };
  const table = WS_CONFIGS[workload];
  if (!table) return { missing: true };
  const config = table[budget];
  if (!config) return { missing: true };
  return {
    workload: workload,
    budget: budget,
    name: WS_WORKLOAD_NAMES[workload] || 'Workstation',
    config: config,
    notes: WS_WORKLOAD_NOTES[workload] || '',
    additional: WS_ADDITIONAL_RECS[workload] || [],
  };
}

// ---------------------------------------------------------------------------
// Converted tool: Upgrade Recommendation.
//
// Technical upgrade guidance only. Per-recommendation cost strings are
// approximate planning estimates, never quotes. No ordering, cart, invoice,
// customer record, or persistence.
// ---------------------------------------------------------------------------
export const UR_RAM_OPTIONS = [
  { value: '4', label: '4GB' },
  { value: '8', label: '8GB' },
  { value: '16', label: '16GB' },
  { value: '32', label: '32GB' },
];

export const UR_STORAGE_OPTIONS = [
  { value: 'HDD', label: 'HDD Only' },
  { value: 'SATA_SSD', label: 'SATA SSD' },
  { value: 'NVME', label: 'NVMe SSD' },
];

export const UR_BUDGET_OPTIONS = [
  { value: '200', label: 'Under 200' },
  { value: '500', label: '200-500' },
  { value: '1000', label: '500-1000' },
  { value: '2000', label: '1000+' },
];

export const UR_ISSUE_OPTIONS = [
  { value: 'slow_boot', label: 'Slow boot times' },
  { value: 'slow_apps', label: 'Applications slow to open' },
  { value: 'low_fps', label: 'Gaming low FPS' },
  { value: 'stuttering', label: 'Gaming stuttering' },
  { value: 'multitask', label: 'Poor multitasking' },
  { value: 'render', label: 'Slow rendering/editing' },
];

// Pure engine. Returns { missing } when a field is blank, otherwise
// { budgetLabel, issueLabel, recommendations, totalCostLow }. Logic
// transcribed faithfully from the source.
export function generateUpgradeRecommendations(inp) {
  const cpu = inp.cpu;
  const gpu = inp.gpu;
  const ram = parseInt(inp.ram, 10);
  const storage = inp.storage;
  const budget = inp.budget;
  const issue = inp.issue;

  if (!cpu || !gpu || !ram || !storage || !budget || !issue) {
    return { missing: true };
  }

  const budgetNum = parseInt(budget, 10);
  const recommendations = [];

  if (storage === 'HDD') {
    recommendations.push({ priority: 1, title: 'Priority: Upgrade to SSD', reason: 'HDD is the number one bottleneck in modern systems', recommendation: 'Install NVMe SSD (500GB-1TB)', cost: '$80-$150', benefit: '500-1000% improvement in boot times, app loading, system responsiveness', notes: 'Clone existing Windows to SSD, keep HDD for storage' });
  } else if (storage === 'SATA_SSD') {
    if (budgetNum >= 500) {
      recommendations.push({ priority: 3, title: 'Consider NVMe SSD upgrade', reason: 'NVMe is 3-5x faster than SATA SSD', recommendation: 'Upgrade to NVMe SSD', cost: '$100-$200', benefit: '3-5x faster storage speeds', notes: 'Good upgrade but not critical' });
    }
  }

  if (ram <= 8) {
    recommendations.push({ priority: 1, title: 'Upgrade RAM to 16GB minimum', reason: '8GB insufficient for modern multitasking and gaming', recommendation: 'Add 8GB or replace with 2x8GB kit', cost: '$50-$90', benefit: '100% improvement in multitasking, eliminates stuttering', notes: 'Match existing RAM speed and type' });
  } else if (ram === 16 && (issue === 'render' || issue === 'multitask')) {
    recommendations.push({ priority: 2, title: 'Upgrade RAM to 32GB', reason: 'Heavy workloads benefit from more RAM', recommendation: 'Upgrade to 32GB (2x16GB)', cost: '$120-$180', benefit: 'Better for video editing, 3D work, heavy multitasking', notes: 'Check motherboard supports 32GB' });
  }

  if (issue === 'low_fps' || issue === 'stuttering') {
    recommendations.push({
      priority: 1,
      title: 'GPU Upgrade for Gaming',
      reason: gpu + ' struggling with modern games',
      recommendation: budgetNum >= 1000 ? 'RTX 4060 Ti or RX 7600 XT' : budgetNum >= 500 ? 'RTX 4060 or RX 7600' : 'Used RTX 3060 or RX 6600',
      cost: budgetNum >= 1000 ? '$500-$700' : budgetNum >= 500 ? '$350-$450' : '$250-$350',
      benefit: 'Double or triple FPS in modern games',
      notes: 'Check PSU wattage sufficient for new GPU',
    });
  }

  const cpuLower = cpu.toLowerCase();
  const isOldCPU = cpuLower.indexOf('i3') !== -1 || cpuLower.indexOf('i5-7') !== -1 || cpuLower.indexOf('i5-6') !== -1 ||
    cpuLower.indexOf('ryzen 3') !== -1 || cpuLower.indexOf('ryzen 5 1') !== -1 || cpuLower.indexOf('ryzen 5 2') !== -1;

  if (isOldCPU && budgetNum >= 500) {
    recommendations.push({
      priority: 2,
      title: 'CPU Upgrade',
      reason: cpu + ' is older generation, limiting performance',
      recommendation: budgetNum >= 1000 ? 'Ryzen 7 7700X or i5-14600K (requires new motherboard + RAM)' : 'Ryzen 5 5600 (if AM4) or i5-12400F (requires new motherboard)',
      cost: budgetNum >= 1000 ? '$600-$900 (CPU+Mobo+RAM)' : '$300-$500 (CPU+Mobo)',
      benefit: '50-100% improvement in multi-threaded tasks',
      notes: 'Major upgrade, requires motherboard and possibly RAM replacement',
    });
  }

  recommendations.sort((a, b) => a.priority - b.priority);

  let totalCostLow = 0;
  for (let i = 0; i < recommendations.length; i++) {
    const match = recommendations[i].cost.match(/\$(\d+)/);
    totalCostLow += (match ? parseInt(match[1], 10) : 0);
  }

  const budgetLabels = { '200': 'Under $200', '500': '$200-$500', '1000': '$500-$1000', '2000': '$1000+' };
  const issueLabels = {
    slow_boot: 'Slow boot times', slow_apps: 'Applications slow to open', low_fps: 'Gaming low FPS',
    stuttering: 'Gaming stuttering', multitask: 'Poor multitasking', render: 'Slow rendering/editing',
  };

  return {
    budgetLabel: budgetLabels[budget] || budget,
    issueLabel: issueLabels[issue] || issue,
    recommendations: recommendations,
    totalCostLow: totalCostLow,
  };
}

// ---------------------------------------------------------------------------
// Converted tool: Build Spec Sheet (technician build documentation).
//
// The source populated component dropdowns from a hardcoded supplier CSV
// catalogue and produced a branded "job sheet" with a customer name and a
// print-quote button. The CSV supplier coupling, the customer record, the
// branded header, and the print-quote behaviour are intentionally not carried
// across. This tool documents a build's components as a plain specification
// summary with manual entry. No pricing, ordering, customer record, or
// persistence.
// ---------------------------------------------------------------------------
export const BUILD_SPEC_FIELDS = [
  { key: 'cpu',         label: 'CPU' },
  { key: 'motherboard', label: 'Motherboard' },
  { key: 'cooler',      label: 'CPU Cooler' },
  { key: 'ram',         label: 'RAM' },
  { key: 'gpu',         label: 'Graphics Card' },
  { key: 'psu',         label: 'Power Supply' },
  { key: 'pcCase',      label: 'Case' },
  { key: 'windows',     label: 'Operating System' },
  { key: 'office',      label: 'Office Software' },
  { key: 'network',     label: 'Networking' },
];

export const BUILD_SPEC_MAX_STORAGE = 4;

// ---------------------------------------------------------------------------
// Converted tool: Refurbished PC Valuation.
//
// Technician valuation estimate only. The source framed output as a "Suggested
// Selling Price" with a warranty and sales-listing tips block; that sales and
// warranty copy is not carried across. Output is a neutral planning estimate,
// never a quote. No sales listing, cart, invoice, quote, or customer record.
// ---------------------------------------------------------------------------
export const REFURB_CPU_GEN = [
  { value: 'current', label: 'Current Gen (13th/14th Intel, Ryzen 7000)' },
  { value: 'last',    label: 'Last Gen (12th Intel, Ryzen 5000)' },
  { value: '2gen',    label: '2 Gens Old (11th Intel, Ryzen 3000)' },
  { value: '3gen',    label: '3 Gens Old (10th Intel, Ryzen 2000)' },
  { value: 'older',   label: '4+ Gens Old' },
];

export const REFURB_CPU_TIER = [
  { value: 'i3', label: 'i3 / Ryzen 3' },
  { value: 'i5', label: 'i5 / Ryzen 5' },
  { value: 'i7', label: 'i7 / Ryzen 7' },
  { value: 'i9', label: 'i9 / Ryzen 9' },
];

export const REFURB_RAM = [
  { value: '8', label: '8GB' },
  { value: '16', label: '16GB' },
  { value: '32', label: '32GB' },
  { value: '64', label: '64GB+' },
];

export const REFURB_STORAGE = [
  { value: 'hdd', label: 'HDD only' },
  { value: 'sata_ssd', label: 'SATA SSD' },
  { value: 'nvme', label: 'NVMe SSD' },
];

export const REFURB_GPU = [
  { value: 'integrated', label: 'Integrated only' },
  { value: 'basic', label: 'Basic dedicated (GTX 1650, etc)' },
  { value: 'mid', label: 'Mid-range (RTX 3060, RX 6700)' },
  { value: 'high', label: 'High-end (RTX 3080+, RX 6800+)' },
];

export const REFURB_CONDITION = [
  { value: 'excellent', label: 'Excellent (like new)' },
  { value: 'good', label: 'Good (minor wear)' },
  { value: 'fair', label: 'Fair (cosmetic damage, works well)' },
  { value: 'poor', label: 'Poor (heavy wear, functional)' },
];

const REFURB_CPU_PRICES = {
  current: { i3: 400, i5: 600, i7: 800, i9: 1000 },
  last: { i3: 300, i5: 450, i7: 600, i9: 750 },
  '2gen': { i3: 200, i5: 300, i7: 400, i9: 550 },
  '3gen': { i3: 150, i5: 200, i7: 300, i9: 400 },
  older: { i3: 100, i5: 150, i7: 200, i9: 250 },
};
const REFURB_RAM_VALUE = { '8': 30, '16': 60, '32': 150, '64': 300 };
const REFURB_STORAGE_VALUE = { hdd: 20, sata_ssd: 50, nvme: 80 };
const REFURB_GPU_VALUE = { integrated: 0, basic: 100, mid: 250, high: 400 };
const REFURB_CONDITION_MULT = { excellent: 1.0, good: 0.85, fair: 0.70, poor: 0.55 };

// Pure engine. Returns { missing } when a field is blank, otherwise the
// valuation breakdown. Logic transcribed faithfully from the source.
export function calculateRefurbValue(inp) {
  const cpuGen = inp.cpuGen;
  const cpuTier = inp.cpuTier;
  const ram = inp.ram;
  const storage = inp.storage;
  const gpu = inp.gpu;
  const condition = inp.condition;

  if (!cpuGen || !cpuTier || !ram || !storage || !gpu || !condition) {
    return { missing: true };
  }

  const cpuValue = REFURB_CPU_PRICES[cpuGen][cpuTier];
  const ramValue = REFURB_RAM_VALUE[ram];
  const storageValue = REFURB_STORAGE_VALUE[storage];
  const gpuValue = REFURB_GPU_VALUE[gpu];
  const subtotal = cpuValue + ramValue + storageValue + gpuValue;
  const conditionMult = REFURB_CONDITION_MULT[condition];
  const finalValue = Math.round(subtotal * conditionMult);
  const minValue = Math.round(finalValue * 0.85);
  const maxValue = Math.round(finalValue * 1.15);

  return {
    cpuValue: cpuValue,
    ramValue: ramValue,
    storageValue: storageValue,
    gpuValue: gpuValue,
    subtotal: subtotal,
    conditionPct: Math.round(conditionMult * 100),
    finalValue: finalValue,
    minValue: minValue,
    maxValue: maxValue,
  };
}

// ---------------------------------------------------------------------------
// Converted tool: Stress Test Protocol (technician stress test summary).
//
// In-session only. The source included Customer Name and Job Number fields, a
// branded report header, and a print-report button; the customer/job record,
// branding, and print behaviour are not carried across. No saved records, job
// history, warranty language, quote, or persistence.
// ---------------------------------------------------------------------------
export const STRESS_TEST_SECTIONS = [
  {
    key: 'cpu', title: 'CPU Stress Test', tool: 'Prime95 or AIDA64', duration: '30-60 minutes',
    fields: [
      { key: 'temp', label: 'Max Temperature (C)', type: 'number', placeholder: 'e.g. 75' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any thermal throttling, crashes, or issues?' },
    ],
  },
  {
    key: 'gpu', title: 'GPU Stress Test', tool: 'FurMark or 3DMark', duration: '30 minutes',
    fields: [
      { key: 'temp', label: 'Max Temperature (C)', type: 'number', placeholder: 'e.g. 80' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any artifacts, crashes, or thermal issues?' },
    ],
  },
  {
    key: 'mem', title: 'Memory Test', tool: 'MemTest86+ or Windows Memory Diagnostic', duration: '1-4 passes',
    fields: [
      { key: 'passes', label: 'Passes Completed', type: 'number', placeholder: 'e.g. 2' },
      { key: 'errors', label: 'Errors Found', type: 'number', placeholder: '0 = No errors' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any memory errors detected?' },
    ],
  },
  {
    key: 'storage', title: 'Storage Test', tool: 'CrystalDiskMark or HD Tune', duration: '10-15 minutes',
    fields: [
      { key: 'read', label: 'Sequential Read (MB/s)', type: 'number', placeholder: 'e.g. 3500' },
      { key: 'write', label: 'Sequential Write (MB/s)', type: 'number', placeholder: 'e.g. 3000' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Performance issues or SMART errors?' },
    ],
  },
  {
    key: 'psu', title: 'Power Supply / Full System', tool: 'Combined stress (CPU and GPU simultaneously)', duration: '15-30 minutes',
    fields: [
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any shutdowns, reboots, or power-related issues?' },
    ],
  },
];

export const STRESS_STATUSES = [
  { value: 'pass', label: 'PASS', mark: '\u2713' },
  { value: 'fail', label: 'FAIL', mark: '\u2717' },
  { value: 'skip', label: 'SKIP', mark: '\u2298' },
];

export const STRESS_VERDICTS = [
  { value: 'pass', label: 'PASS - System Stable' },
  { value: 'pass_notes', label: 'PASS - With Notes' },
  { value: 'fail', label: 'FAIL - Issues Found' },
];

// ---------------------------------------------------------------------------
// Converted tool: QC Checklist (technician QC summary).
//
// In-session only. The source included Customer Name and Job Number fields, a
// branded "Quality Control Certificate" header, and a print-certificate
// button; those are not carried across, and two item labels were neutralised
// to remove invoice and customer-pickup framing. No saved records, job
// history, warranty language, quote, or persistence.
//
// Note: the source hardcoded a total of 39 checks while defining 32; the
// completion total here is computed from the actual item count so completion
// can correctly reach 100 percent.
// ---------------------------------------------------------------------------
export const QC_CATEGORIES = [
  {
    id: 'hw', title: 'Hardware Checks', icon: '\u{1F527}',
    items: [
      'All components properly installed and seated',
      'All cables connected correctly',
      'RAM detected at correct speed/capacity',
      'Storage drives detected and accessible',
      'GPU detected (if applicable)',
      'All fans spinning correctly',
      'No unusual noises or vibrations',
    ],
  },
  {
    id: 'bios', title: 'BIOS / Boot', icon: '\u2699',
    items: [
      'BIOS settings optimized (XMP/DOCP enabled if applicable)',
      'Boot order configured correctly',
      'System boots to Windows successfully',
      'No POST errors or warnings',
    ],
  },
  {
    id: 'sw', title: 'Software', icon: '\u{1F4BE}',
    items: [
      'Windows activated',
      'All Windows updates installed',
      'All drivers installed (chipset, GPU, audio, network)',
      'Antivirus active and updated',
      'No bloatware or unnecessary software',
    ],
  },
  {
    id: 'perf', title: 'Performance', icon: '\u26A1',
    items: [
      'CPU temperatures normal under load',
      'GPU temperatures normal under load',
      'Storage read/write speeds acceptable',
      'Internet/WiFi connection tested',
      'No thermal throttling observed',
    ],
  },
  {
    id: 'conn', title: 'Connectivity', icon: '\u{1F50C}',
    items: [
      'All USB ports tested',
      'Audio output tested (headphone/speakers)',
      'Display output tested (all ports if multiple)',
      'Network connectivity verified',
    ],
  },
  {
    id: 'phys', title: 'Physical / Cosmetic', icon: '\u2728',
    items: [
      'Case cleaned inside and out',
      'All panels/screws secured',
      'Cable management tidy',
      'No visible damage or defects',
    ],
  },
  {
    id: 'final', title: 'Final Preparation', icon: '\u{1F4E6}',
    items: [
      'All accessories included (cables, manuals, etc.)',
      'Documentation prepared',
      'System ready for handover',
    ],
  },
];

export function qcTotalItems() {
  let n = 0;
  for (let i = 0; i < QC_CATEGORIES.length; i++) n += QC_CATEGORIES[i].items.length;
  return n;
}

// ---------------------------------------------------------------------------
// Converted tool: Repair Checklist (technician repair-step checklists).
//
// In-session only. The source included Customer Name and Job Number fields, a
// branded output header, and a print-checklist button; those are not carried
// across. A few item labels were neutralised to remove customer-record and
// shop framing (reported in the stage notes). No saved records, job history,
// warranty language, quote, or persistence.
// ---------------------------------------------------------------------------
export const REPAIR_TYPES = [
  { value: 'virus', label: 'Virus Removal' },
  { value: 'hardware', label: 'Hardware Upgrade' },
  { value: 'general', label: 'General Service' },
  { value: 'build', label: 'Custom Build' },
  { value: 'data', label: 'Data Recovery' },
];

export const REPAIR_CHECKLISTS = {
            virus: {
                title: "Virus Removal Checklist",
                categories: [
                    {
                        name: "Pre-Repair Assessment",
                        items: [
                            { title: "Document reported symptoms", description: "Record the issues reported" },
                            { title: "Check current antivirus status", description: "Note what AV is installed, if any" },
                            { title: "Test internet connectivity", description: "Verify network connection works" },
                            { title: "Create backup if possible", description: "Backup important data before cleaning" }
                        ]
                    },
                    {
                        name: "Virus Removal Process",
                        items: [
                            { title: "Boot to Safe Mode with Networking", description: "F8 or Shift+Restart method" },
                            { title: "Run MalwareBytes full scan", description: "Let complete scan finish" },
                            { title: "Run AdwCleaner", description: "Remove adware and PUPs" },
                            { title: "Run HitmanPro or similar", description: "Second opinion scan" },
                            { title: "Remove suspicious programs", description: "Check Programs & Features" },
                            { title: "Remove suspicious browser extensions", description: "All browsers checked" },
                            { title: "Reset browsers to default", description: "Chrome, Firefox, Edge" },
                            { title: "Check startup programs", description: "Disable suspicious entries" },
                            { title: "Check scheduled tasks", description: "Remove malicious tasks" },
                            { title: "Run SFC scan", description: "sfc /scannow to repair system files" }
                        ]
                    },
                    {
                        name: "Post-Removal Verification",
                        items: [
                            { title: "Reboot to normal mode", description: "Test system boots normally" },
                            { title: "Run final scan in normal mode", description: "Verify no threats remain" },
                            { title: "Test internet browsing", description: "No redirects or pop-ups" },
                            { title: "Update Windows", description: "Install all pending updates" },
                            { title: "Install/update antivirus", description: "Ensure protection active" },
                            { title: "Test all common programs", description: "Office, email, etc work properly" }
                        ]
                    },
                    {
                        name: "Final Quality Checks",
                        items: [
                            { title: "Check startup time", description: "Should boot in reasonable time" },
                            { title: "Check CPU/RAM usage", description: "Task Manager shows normal levels" },
                            { title: "Document what was removed", description: "For records" },
                            { title: "Clean desktop and temp files", description: "Remove scan logs, temp files" }
                        ]
                    }
                ]
            },
            hardware: {
                title: "Hardware Upgrade Checklist",
                categories: [
                    {
                        name: "Pre-Installation",
                        items: [
                            { title: "Verify parts compatibility", description: "Double-check specs match" },
                            { title: "Document current hardware", description: "Record what's being replaced" },
                            { title: "Create backup", description: "Full system backup if possible" },
                            { title: "Prepare workspace", description: "Anti-static mat, proper lighting" },
                            { title: "Photo document before work", description: "Take photos of cable routing" }
                        ]
                    },
                    {
                        name: "Installation Process",
                        items: [
                            { title: "Power off and unplug", description: "Wait 30 seconds after unplugging" },
                            { title: "Remove old hardware carefully", description: "Keep screws organized" },
                            { title: "Install new hardware properly", description: "Firmly seated, all connections" },
                            { title: "Connect all cables", description: "Power, data cables secure" },
                            { title: "Route cables neatly", description: "Good airflow, no pinching" },
                            { title: "Apply thermal paste if CPU", description: "Pea-sized dot, even spread" }
                        ]
                    },
                    {
                        name: "Post-Installation Testing",
                        items: [
                            { title: "Visual inspection", description: "Check all connections before power on" },
                            { title: "Power on and enter BIOS", description: "Verify hardware detected" },
                            { title: "Boot to Windows", description: "Check boot successful" },
                            { title: "Install drivers if needed", description: "Latest from manufacturer" },
                            { title: "Run hardware test", description: "Stress test if RAM/CPU/GPU" },
                            { title: "Check temperatures", description: "HWiNFO64 - temps in range" },
                            { title: "Benchmark if applicable", description: "UserBenchmark or similar" }
                        ]
                    },
                    {
                        name: "Final Checks",
                        items: [
                            { title: "All fans working", description: "CPU, GPU, case fans spinning" },
                            { title: "No unusual noises", description: "Listen for grinding, clicking" },
                            { title: "Cable management complete", description: "Clean, professional appearance" },
                            { title: "Update documentation", description: "Record new hardware specs" }
                        ]
                    }
                ]
            },
            general: {
                title: "General Service Checklist",
                categories: [
                    {
                        name: "Initial Checks",
                        items: [
                            { title: "Document reported complaint", description: "Detailed notes on issues" },
                            { title: "Visual inspection", description: "Check for physical damage, dust" },
                            { title: "Test power on", description: "Verify system boots" },
                            { title: "Check BIOS date/time", description: "May indicate CMOS battery" }
                        ]
                    },
                    {
                        name: "System Cleaning",
                        items: [
                            { title: "Power off and unplug", description: "Safety first" },
                            { title: "Clean dust from case", description: "Compressed air, all components" },
                            { title: "Clean CPU heatsink", description: "Remove dust between fins" },
                            { title: "Clean GPU fans", description: "Dust on fans affects cooling" },
                            { title: "Clean case fans", description: "Intake and exhaust fans" },
                            { title: "Clean keyboard", description: "Compressed air, cloth" },
                            { title: "Clean monitor", description: "Screen cleaner, microfiber" }
                        ]
                    },
                    {
                        name: "Software Maintenance",
                        items: [
                            { title: "Run Windows Update", description: "Install all pending updates" },
                            { title: "Update drivers", description: "Graphics, chipset, network" },
                            { title: "Run virus scan", description: "Full MalwareBytes scan" },
                            { title: "Disk Cleanup", description: "Remove temp files, old updates" },
                            { title: "Defragment if HDD", description: "Skip if SSD" },
                            { title: "Check disk health", description: "CrystalDiskInfo" },
                            { title: "Optimize startup", description: "Disable unnecessary programs" }
                        ]
                    },
                    {
                        name: "Performance Testing",
                        items: [
                            { title: "Check boot time", description: "Should be under 60 seconds" },
                            { title: "Check temperatures", description: "Idle and under load" },
                            { title: "Check RAM usage", description: "Idle should be reasonable" },
                            { title: "Test common applications", description: "Browser, Office, etc" }
                        ]
                    }
                ]
            },
            build: {
                title: "Custom Build Checklist",
                categories: [
                    {
                        name: "Parts Verification",
                        items: [
                            { title: "Verify all parts received", description: "Check against build list" },
                            { title: "Inspect for shipping damage", description: "Check boxes and components" },
                            { title: "Compatibility double-check", description: "CPU/Mobo, RAM speed, PSU wattage" },
                            { title: "Organize parts and tools", description: "Clean workspace ready" }
                        ]
                    },
                    {
                        name: "Build Process",
                        items: [
                            { title: "Install I/O shield", description: "Before motherboard!" },
                            { title: "Install motherboard standoffs", description: "Correct positions only" },
                            { title: "Install CPU", description: "Align marker, no force needed" },
                            { title: "Apply thermal paste", description: "Pea-sized dot center" },
                            { title: "Install CPU cooler", description: "Firmly attached, even pressure" },
                            { title: "Install RAM", description: "Slots per manual (usually 2 and 4)" },
                            { title: "Install motherboard", description: "All standoffs aligned" },
                            { title: "Connect front panel", description: "Power, reset, LEDs per manual" },
                            { title: "Install PSU", description: "Fan orientation correct" },
                            { title: "Connect 24-pin power", description: "Firmly clicked in" },
                            { title: "Connect 4+4 pin CPU power", description: "Top left of motherboard" },
                            { title: "Install storage drives", description: "SSD/HDD mounted securely" },
                            { title: "Connect SATA cables", description: "Data and power" },
                            { title: "Install GPU", description: "PCIe lock clicked, power connected" },
                            { title: "Install case fans", description: "Intake front, exhaust rear/top" },
                            { title: "Cable management", description: "Route behind motherboard tray" }
                        ]
                    },
                    {
                        name: "First Boot Testing",
                        items: [
                            { title: "Double-check all connections", description: "Before power on" },
                            { title: "Power on outside case test", description: "Optional but recommended" },
                            { title: "Power on and enter BIOS", description: "Check all hardware detected" },
                            { title: "Check CPU temperature", description: "Should be reasonable" },
                            { title: "Check RAM amount", description: "Full capacity showing" },
                            { title: "Check boot order", description: "Set correctly" },
                            { title: "Enable XMP/DOCP", description: "For rated RAM speed" }
                        ]
                    },
                    {
                        name: "OS Installation & Testing",
                        items: [
                            { title: "Install Windows", description: "Latest version from USB" },
                            { title: "Activate Windows", description: "Enter product key" },
                            { title: "Install chipset drivers", description: "From motherboard manufacturer" },
                            { title: "Install GPU drivers", description: "Latest from NVIDIA/AMD" },
                            { title: "Install other drivers", description: "Audio, network, etc" },
                            { title: "Run Windows Update", description: "Install all updates" },
                            { title: "Install requested software", description: "Per request" },
                            { title: "Run stress tests", description: "Prime95, FurMark if requested" },
                            { title: "Monitor temps under load", description: "Should be in safe range" },
                            { title: "Run benchmark", description: "UserBenchmark for documentation" }
                        ]
                    },
                    {
                        name: "Final QC",
                        items: [
                            { title: "All fans working", description: "Spin freely, no noise" },
                            { title: "No POST errors", description: "Clean boot every time" },
                            { title: "RGB lighting working", description: "If applicable" },
                            { title: "Case buttons functional", description: "Power, reset" },
                            { title: "USB ports tested", description: "Front and rear" },
                            { title: "Audio tested", description: "Front and rear jacks" },
                            { title: "Close case panels", description: "All screws tight" },
                            { title: "External cleaning", description: "Fingerprints, dust removed" },
                            { title: "Create build documentation", description: "Specs, benchmarks, photos" }
                        ]
                    }
                ]
            },
            data: {
                title: "Data Recovery Checklist",
                categories: [
                    {
                        name: "Initial Assessment",
                        items: [
                            { title: "Document required data", description: "What data is critical" },
                            { title: "Assess drive condition", description: "Physical damage? Clicking?" },
                            { title: "Determine recovery urgency", description: "Timeline expectations" },
                            { title: "Check if drive detected", description: "BIOS, Disk Management" },
                            { title: "Warning: Don't run chkdsk", description: "Can make recovery harder" }
                        ]
                    },
                    {
                        name: "Recovery Process",
                        items: [
                            { title: "Create drive image if possible", description: "Work from image, not original" },
                            { title: "Run data recovery software", description: "Recuva, R-Studio, PhotoRec" },
                            { title: "Document recovered files", description: "List what was found" },
                            { title: "Organize recovered data", description: "By type, folder structure" },
                            { title: "Verify file integrity", description: "Check files can be opened" }
                        ]
                    },
                    {
                        name: "Data Delivery",
                        items: [
                            { title: "Copy to owner's media", description: "External drive or cloud" },
                            { title: "Verify all files copied", description: "Check file count, sizes" },
                            { title: "Create recovery report", description: "What recovered, what lost" },
                            { title: "Secure data deletion after transfer", description: "Privacy" }
                        ]
                    }
                ]
            }
};

// ---------------------------------------------------------------------------
// Converted tool: Burn-In Tracker (technician burn-in log).
//
// In-session only. The source included Customer Name and Job Number fields, a
// branded report header, and a print-report button; those are not carried
// across. Timestamps use the current date only; there is no live timer, saved
// record, job history, warranty language, quote, or persistence.
// ---------------------------------------------------------------------------
export const BURNIN_DURATIONS = [
  { value: '1', label: '1 Hour' },
  { value: '2', label: '2 Hours' },
  { value: '3', label: '3 Hours' },
  { value: '4', label: '4 Hours' },
  { value: '5', label: '5 Hours' },
  { value: '10', label: '10 Hours' },
  { value: '24', label: '24 Hours' },
  { value: '48', label: '48 Hours' },
  { value: '72', label: '72 Hours' },
];

export const BURNIN_OVERALL_STATUS = [
  { value: 'pass', label: 'PASS - System Stable' },
  { value: 'pass_notes', label: 'PASS - With Notes' },
  { value: 'fail', label: 'FAIL - Issues Found' },
];

export const BURNIN_RUN_STATUS = [
  { value: 'pass', label: 'PASS - Test Completed', color: '#4ade80', mark: '\u2713' },
  { value: 'warn', label: 'WARNING - Minor Issues', color: '#fbbf24', mark: '\u26A0' },
  { value: 'fail', label: 'FAIL - Test Failed', color: '#f87171', mark: '\u2717' },
];

// Per-program field definitions. type is number/text/select/textarea. Fields
// are faithful to the source program set.
export const BURNIN_PROGRAMS = [
  { value: 'cinebench', label: 'Cinebench R15', fields: [
    { key: 'single', label: 'Single-Core Score (pts)', type: 'number' },
    { key: 'multi', label: 'Multi-Core Score (pts)', type: 'number' },
    { key: 'opengl', label: 'OpenGL FPS (optional)', type: 'number' },
  ] },
  { value: 'cpubench', label: 'CPU Benchmark', fields: [
    { key: 'score', label: 'CPU Score', type: 'number' },
    { key: 'rating', label: 'Performance Rating', type: 'select', options: [
      { value: 'excellent', label: 'Excellent' }, { value: 'good', label: 'Good' }, { value: 'average', label: 'Average' }, { value: 'below', label: 'Below Average' },
    ] },
  ] },
  { value: 'novabench', label: 'NovaBench', fields: [
    { key: 'total', label: 'Total Score', type: 'number' },
    { key: 'cpu', label: 'CPU Score', type: 'number' },
    { key: 'gpu', label: 'GPU Score', type: 'number' },
    { key: 'ram', label: 'RAM Score (MB/s)', type: 'number' },
    { key: 'disk', label: 'Disk Write Speed (MB/s)', type: 'number' },
  ] },
  { value: 'userbench', label: 'UserBenchmark', fields: [
    { key: 'gaming', label: 'Gaming %', type: 'number' },
    { key: 'desktop', label: 'Desktop %', type: 'number' },
    { key: 'work', label: 'Workstation %', type: 'number' },
    { key: 'percentile', label: 'Overall Percentile', type: 'number' },
  ] },
  { value: 'unigine', label: 'Unigine Valley', fields: [
    { key: 'score', label: 'Score', type: 'number' },
    { key: 'fps', label: 'FPS', type: 'number' },
    { key: 'minfps', label: 'Min FPS', type: 'number' },
    { key: 'maxfps', label: 'Max FPS', type: 'number' },
  ] },
  { value: 'aida64', label: 'AIDA64 Extreme', fields: [
    { key: 'queen', label: 'CPU Queen Score', type: 'number' },
    { key: 'julia', label: 'FPU Julia Score', type: 'number' },
    { key: 'memread', label: 'Memory Read (MB/s)', type: 'number' },
  ] },
  { value: 'hwinfo', label: 'HWiNFO64', fields: [
    { key: 'cputemp', label: 'Max CPU Temp (C)', type: 'number' },
    { key: 'gputemp', label: 'Max GPU Temp (C)', type: 'number' },
    { key: 'cpuclock', label: 'Max CPU Clock (MHz)', type: 'number' },
  ] },
  { value: 'openhw', label: 'Open Hardware Monitor', fields: [
    { key: 'cputemp', label: 'CPU Temp (C)', type: 'number' },
    { key: 'gputemp', label: 'GPU Temp (C)', type: 'number' },
    { key: 'systemp', label: 'System Temp (C)', type: 'number' },
  ] },
  { value: 'iobit', label: 'IObit ToolBox', fields: [
    { key: 'result', label: 'System Optimization Result', type: 'select', options: [
      { value: 'optimal', label: 'Optimal - No Issues' }, { value: 'minor', label: 'Minor Issues Fixed' }, { value: 'major', label: 'Major Issues Fixed' },
    ] },
    { key: 'issues', label: 'Issues Found', type: 'textarea' },
  ] },
  { value: 'bitpro', label: 'Burn In Test Pro', fields: [
    { key: 'duration', label: 'Test Duration (minutes)', type: 'number' },
    { key: 'errors', label: 'Errors Found', type: 'number' },
    { key: 'result', label: 'Result', type: 'select', options: [
      { value: 'pass', label: 'PASS - No Errors' }, { value: 'fail', label: 'FAIL - Errors Found' },
    ] },
  ] },
  { value: 'diskbench', label: 'DiskBench', fields: [
    { key: 'seqread', label: 'Sequential Read (MB/s)', type: 'number' },
    { key: 'seqwrite', label: 'Sequential Write (MB/s)', type: 'number' },
  ] },
  { value: 'diskinfo', label: 'CrystalDiskInfo', fields: [
    { key: 'health', label: 'Health Status', type: 'select', options: [
      { value: 'good', label: 'Good' }, { value: 'caution', label: 'Caution' }, { value: 'bad', label: 'Bad' },
    ] },
    { key: 'temp', label: 'Temperature (C)', type: 'number' },
    { key: 'hours', label: 'Power On Hours', type: 'number' },
  ] },
  { value: 'diskmark', label: 'CrystalDiskMark', fields: [
    { key: 'seqread', label: 'Seq Read (MB/s)', type: 'number' },
    { key: 'seqwrite', label: 'Seq Write (MB/s)', type: 'number' },
    { key: 'r4k', label: '4K Read (MB/s)', type: 'number' },
    { key: 'w4k', label: '4K Write (MB/s)', type: 'number' },
  ] },
  { value: 'cpuz', label: 'CPU-Z 64-bit', fields: [
    { key: 'single', label: 'Single Thread Score', type: 'number' },
    { key: 'multi', label: 'Multi Thread Score', type: 'number' },
    { key: 'freq', label: 'CPU Frequency (MHz)', type: 'number' },
  ] },
  { value: 'gpuz', label: 'GPU-Z', fields: [
    { key: 'gpuclock', label: 'GPU Clock (MHz)', type: 'number' },
    { key: 'memclock', label: 'Memory Clock (MHz)', type: 'number' },
    { key: 'temp', label: 'Temperature (C)', type: 'number' },
  ] },
  { value: 'hwmon', label: 'HWMonitor', fields: [
    { key: 'cputemp', label: 'Max CPU Temp (C)', type: 'number' },
    { key: 'gputemp', label: 'Max GPU Temp (C)', type: 'number' },
    { key: 'voltage', label: 'Max Voltage (V)', type: 'number' },
  ] },
  { value: 'coretemp', label: 'Core Temp', fields: [
    { key: 'maxtemp', label: 'Max Temp (C)', type: 'number' },
    { key: 'avgtemp', label: 'Avg Temp (C)', type: 'number' },
    { key: 'tjmax', label: 'TJ Max (C)', type: 'number' },
  ] },
  { value: 'ddu', label: 'Display Driver Uninstaller', fields: [
    { key: 'driver', label: 'Driver Removed', type: 'text' },
    { key: 'result', label: 'Result', type: 'select', options: [
      { value: 'success', label: 'Success - Clean Removal' }, { value: 'partial', label: 'Partial - Some Files Remain' }, { value: 'failed', label: 'Failed - Driver Not Removed' },
    ] },
  ] },
  { value: 'ibt', label: 'Intel Burn Test', fields: [
    { key: 'gflops', label: 'GFlops Score', type: 'number' },
    { key: 'passes', label: 'Number of Passes', type: 'number' },
    { key: 'maxtemp', label: 'Max Temp (C)', type: 'number' },
    { key: 'result', label: 'Result', type: 'select', options: [
      { value: 'pass', label: 'PASS - All Tests Passed' }, { value: 'fail', label: 'FAIL - Test Failed' },
    ] },
  ] },
  { value: 'hwinfotest', label: 'HWiNFO64 (Testing)', fields: [
    { key: 'cputemp', label: 'Max CPU Temp (C)', type: 'number' },
    { key: 'gputemp', label: 'Max GPU Temp (C)', type: 'number' },
    { key: 'cpuclock', label: 'Max CPU Clock (MHz)', type: 'number' },
  ] },
];

export function getBurninProgram(value) {
  for (let i = 0; i < BURNIN_PROGRAMS.length; i++) {
    if (BURNIN_PROGRAMS[i].value === value) return BURNIN_PROGRAMS[i];
  }
  return null;
}

// ---------------------------------------------------------------------------
// Backup Verification data (technician backup verification summary).
//
// Converted from a source that produced a branded "Backup Verification
// Certificate" with a Customer Name field, a hardcoded "Verified By:
// Technogeek Computers" line, a shop footer (location and email), a
// MASTER_HUB link, and a print button. None of that is carried across. The
// output is reframed as an in-session "Technician Backup Verification
// Summary" with a "Not a customer document" disclaimer. The source location
// label "Our Server" is neutralised to "Server". The schedule option
// "Manual (customer initiated)" is neutralised to "Manual (user/owner
// initiated)". No saved records, certificate, warranty, download, or
// persistence.
// ---------------------------------------------------------------------------
export const BACKUP_LOCATIONS = [
  { value: 'server', label: 'Server' },
  { value: 'external', label: 'External Drive' },
  { value: 'internal', label: 'Internal (PC to PC Direct)' },
];

export const BACKUP_SERVER_TYPES = [
  { value: 'data_pc', label: 'Data PC' },
  { value: 'nas', label: 'NAS' },
];

export const BACKUP_DRIVE_TYPES = [
  { value: 'ssd', label: 'SSD' },
  { value: 'hdd', label: 'HDD' },
];

export const BACKUP_CONNECTION_TYPES = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

export const BACKUP_SSD_SIZES = ['240GB', '256GB', '480GB', '500GB', '512GB', '1TB', '2TB', '4TB'];
export const BACKUP_HDD_SIZES = ['500GB', '1TB', '2TB', '3TB', '4TB', '6TB', '8TB'];

export function getBackupDriveSizes(driveType) {
  if (driveType === 'ssd') return BACKUP_SSD_SIZES;
  if (driveType === 'hdd') return BACKUP_HDD_SIZES;
  return [];
}

export const BACKUP_FOLDERS = [
  { key: 'desktop', label: 'Desktop' },
  { key: 'documents', label: 'Documents' },
  { key: 'downloads', label: 'Downloads' },
  { key: 'pictures', label: 'Pictures' },
  { key: 'videos', label: 'Videos' },
  { key: 'music', label: 'Music' },
  { key: 'favorites', label: 'Favorites' },
  { key: 'appdata_local', label: 'AppData\\Local' },
  { key: 'appdata_roaming', label: 'AppData\\Roaming' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'links', label: 'Links' },
  { key: 'saved_games', label: 'Saved Games' },
  { key: 'searches', label: 'Searches' },
  { key: 'onedrive', label: 'OneDrive' },
];

export const BACKUP_VERIFICATION_TESTS = [
  { value: 'file_check', label: 'File Count & Size Verification' },
  { value: 'sample_restore', label: 'Sample File Restore Test' },
  { value: 'full_restore', label: 'Full Restore Test' },
  { value: 'checksum', label: 'Checksum Verification' },
];

export const BACKUP_RESULTS = [
  { value: 'pass', label: 'PASS - All files verified', color: '#4ade80', mark: '\u2713', headline: 'VERIFIED SUCCESSFUL' },
  { value: 'pass_warnings', label: 'PASS - Minor warnings', color: '#fbbf24', mark: '\u26A0', headline: 'VERIFIED WITH WARNINGS' },
  { value: 'fail', label: 'FAIL - Issues found', color: '#f87171', mark: '\u2717', headline: 'REQUIRES ATTENTION' },
];

export function getBackupResult(value) {
  for (let i = 0; i < BACKUP_RESULTS.length; i++) {
    if (BACKUP_RESULTS[i].value === value) return BACKUP_RESULTS[i];
  }
  return null;
}

export const BACKUP_SCHEDULES = [
  { value: 'manual', label: 'Manual (user/owner initiated)' },
  { value: 'daily', label: 'Daily automatic backup' },
  { value: 'weekly', label: 'Weekly automatic backup' },
  { value: 'continuous', label: 'Continuous sync' },
];

export function getBackupSchedule(value) {
  for (let i = 0; i < BACKUP_SCHEDULES.length; i++) {
    if (BACKUP_SCHEDULES[i].value === value) return BACKUP_SCHEDULES[i];
  }
  return null;
}

// ---------------------------------------------------------------------------
// Customer Handover data (technician handover summary).
//
// Converted from a source that produced a branded customer-facing "Service
// Completion" document with Customer Name and Job Number fields, three
// plaintext credential inputs (Windows / BIOS / other) that were rendered
// into the output, a Warranty Information field with a "Standard warranty
// terms apply" fallback, a "Thank You for Choosing Technogeek" marketing
// block, a shop footer (location and email), a MASTER_HUB link, and a
// "Print for Customer" button. Per locked boundary decisions: customer and
// job identity fields are dropped in favour of a "Prepared By / Technician"
// field; ALL password inputs are removed and replaced with safe checklist
// confirmations only (nothing entered, rendered, or stored); the warranty
// section is removed entirely. Output is reframed as an in-session
// "Technician Handover Summary" with a "Not a customer document" disclaimer.
// The checklist item "Customer data verified/restored" is neutralised to
// "Owner/user data verified/restored". No saved records, certificate,
// warranty, download, or persistence.
// ---------------------------------------------------------------------------
export const HANDOVER_CHECKLIST = [
  'System powers on correctly',
  'All hardware functioning properly',
  'Operating system boots successfully',
  'Internet/WiFi connection tested',
  'All drivers installed and updated',
  'Windows updates current',
  'Antivirus active and updated',
  'Owner/user data verified/restored',
  'System cleaned (internal/external)',
  'All cables/accessories included',
];

export const HANDOVER_CREDENTIAL_ITEMS = [
  'Credentials handed over separately',
  'Owner advised to change passwords',
  'No passwords stored in VISINVOKE',
  'Password manager recommended',
  'Recovery details returned to owner',
];

// ---------------------------------------------------------------------------
// Data Recovery Assessment data + engine (technician assessment).
//
// Converted from a source that produced a branded "Data Recovery Quote" with
// Customer Name / Phone / Email fields, a monetary pricing engine (base fees,
// damage surcharges, urgency multiplier), an "ESTIMATED TOTAL" in dollars, a
// diagnostic-fee / payment-before-release "Important Terms" block, an "Our
// Recovery Process" sales funnel, a shop footer, a MASTER_HUB link, a print
// button, and a Quote #DRQ-xxxxxx number. Per the locked Data & Security
// boundary, ALL pricing and quote behaviour is stripped. What remains is a
// non-monetary technician assessment: recovery difficulty, recovery
// likelihood, handling risk, and recommended next steps. Urgency is retained
// only as turnaround context, never as a price multiplier. No customer
// record, quote, invoice, certificate, download, or persistence.
// ---------------------------------------------------------------------------
export const DR_DEVICE_TYPES = [
  { value: 'hdd', label: 'Hard Disk Drive (HDD)' },
  { value: 'ssd', label: 'Solid State Drive (SSD)' },
  { value: 'usb', label: 'USB Flash Drive' },
  { value: 'sd', label: 'SD / Memory Card' },
  { value: 'raid', label: 'RAID Array' },
  { value: 'nas', label: 'NAS Device' },
  { value: 'mobile', label: 'Mobile Device' },
];

export const DR_DAMAGE_LEVELS = [
  { value: 'logical', label: 'Logical (deletion, corruption, formatting)' },
  { value: 'physical', label: 'Physical (clicking, not detected, dropped)' },
  { value: 'severe', label: 'Severe (fire, water, electrical)' },
  { value: 'raid', label: 'RAID / Array Failure (multiple drive failure)' },
];

export const DR_URGENCY = [
  { value: 'standard', label: 'Standard (5-10 business days)' },
  { value: 'priority', label: 'Priority (2-3 business days)' },
  { value: 'emergency', label: 'Emergency (24-48 hours)' },
];

export const DR_SCOPE = [
  { value: 'partial', label: 'Partial (specific files/folders)' },
  { value: 'full', label: 'Full (everything possible)' },
];

export function getRecoverySteps(damage) {
  if (damage === 'logical') {
    return [
      'Stop writing to the affected drive immediately to avoid overwriting recoverable data.',
      'Create a sector-by-sector image of the drive and work only from the image.',
      'Run file-recovery tools against the image, not the original media.',
      'Verify recovered files open correctly before returning the drive to service.',
    ];
  }
  if (damage === 'physical') {
    return [
      'Do not repeatedly power-cycle the drive; further spin-up attempts can worsen damage.',
      'Do not open the drive outside a suitable clean environment.',
      'If the drive is still detected, image it immediately before any further work.',
      'For clicking or head-related faults, consider a specialist recovery referral.',
    ];
  }
  if (damage === 'severe') {
    return [
      'Do not power on a fire, water, or electrically damaged device.',
      'Keep water-damaged media stable; do not attempt to dry and power on.',
      'Specialist clean-room recovery is strongly recommended for this damage class.',
      'Document the contamination and handling for the recovery specialist.',
    ];
  }
  if (damage === 'raid') {
    return [
      'Do not rebuild or resync the array; a rebuild can overwrite recoverable data.',
      'Record the array type, disk order, and controller configuration before touching disks.',
      'Image every member disk individually before any reconstruction attempt.',
      'Reconstruct the array virtually from the images rather than on the original disks.',
    ];
  }
  return ['Select a device type and damage level to see recommended next steps.'];
}

// Pure assessment engine. Data-in/data-out. No pricing, no quote, no
// persistence. Returns non-monetary difficulty / likelihood / handling-risk
// levels plus recommended steps.
export function assessDataRecovery(inp) {
  const device = inp && inp.deviceType ? inp.deviceType : '';
  const damage = inp && inp.damageLevel ? inp.damageLevel : '';

  const deviceWeight = { usb: 0, sd: 0, hdd: 1, nas: 2, mobile: 2, ssd: 2, raid: 3 };
  const damageWeight = { logical: 0, physical: 2, severe: 3, raid: 2 };
  const dw = typeof deviceWeight[device] === 'number' ? deviceWeight[device] : 1;
  const gw = typeof damageWeight[damage] === 'number' ? damageWeight[damage] : 1;
  let score = dw + gw;
  if (device === 'ssd' && (damage === 'physical' || damage === 'severe')) score += 1;

  let difficulty = 'Moderate';
  if (score <= 1) difficulty = 'Low';
  else if (score <= 3) difficulty = 'Moderate';
  else if (score <= 4) difficulty = 'High';
  else difficulty = 'Very High';

  let likelihood = 'Uncertain';
  if (damage === 'logical') likelihood = 'High';
  else if (damage === 'physical') likelihood = 'Moderate';
  else if (damage === 'raid') likelihood = 'Variable';
  else if (damage === 'severe') likelihood = 'Low';
  if (device === 'ssd' && damage === 'logical') likelihood = 'Moderate';

  let risk = 'Moderate';
  if (damage === 'logical') risk = 'Low to Moderate';
  else if (damage === 'physical') risk = 'High';
  else if (damage === 'severe') risk = 'Critical';
  else if (damage === 'raid') risk = 'High';

  const difficultyColor = difficulty === 'Low' ? '#4ade80' : difficulty === 'Moderate' ? '#fbbf24' : difficulty === 'High' ? '#fb923c' : '#f87171';
  const likelihoodColor = likelihood === 'High' ? '#4ade80' : likelihood === 'Moderate' ? '#fbbf24' : likelihood === 'Variable' ? '#fb923c' : likelihood === 'Low' ? '#f87171' : '#94a3b8';
  const riskColor = risk === 'Low to Moderate' ? '#4ade80' : risk === 'Moderate' ? '#fbbf24' : risk === 'High' ? '#fb923c' : '#f87171';

  return {
    difficulty: difficulty, difficultyColor: difficultyColor,
    likelihood: likelihood, likelihoodColor: likelihoodColor,
    risk: risk, riskColor: riskColor,
    steps: getRecoverySteps(damage),
  };
}

// ---------------------------------------------------------------------------
// Security Audit data + engine (technician security audit summary).
//
// Converted from a source that produced a branded "Security Audit Report"
// with a Customer/Company Name field, a shop footer, a MASTER_HUB link, a
// print button, and an Audit #SEC-xxxxxx number. None of that is carried
// across. Output is reframed as an in-session "Technician Security Audit
// Summary" with a "Not a customer document" disclaimer. The source hardcoded
// totalChecks to 34 while defining only 29 checkboxes; the total is now
// computed dynamically from the category items (29), fixing that defect. The
// checklist items are yes/no controls only; no password or credential values
// are entered, displayed, or stored. No saved report, certificate, customer
// record, ticketing, monitoring, or persistence.
// ---------------------------------------------------------------------------
export const SEC_CATEGORIES = [
  { id: 'win', icon: '\u{1F6E1}', title: 'Windows Security', items: [
    'Windows Defender or antivirus active and updated',
    'Windows Firewall enabled',
    'All Windows security updates installed',
    'BitLocker or disk encryption enabled (if applicable)',
    'Ransomware protection enabled',
  ] },
  { id: 'net', icon: '\u{1F310}', title: 'Network Security', items: [
    'WiFi using WPA2/WPA3 encryption',
    'Strong WiFi password (12+ characters)',
    'Router firmware up to date',
    'Default router admin password changed',
    'Guest network configured (if needed)',
    'UPnP disabled on router (unless required)',
  ] },
  { id: 'user', icon: '\u{1F464}', title: 'User Accounts & Passwords', items: [
    'Strong passwords on all accounts (12+ characters)',
    'Standard user accounts for daily use (not admin)',
    'Windows login required (no auto-login)',
    'Guest account disabled',
    'Multi-factor authentication enabled (where possible)',
  ] },
  { id: 'phys', icon: '\u{1F510}', title: 'Physical Security', items: [
    'Screen lock or screensaver with password (5-10 min timeout)',
    'BIOS password set (prevents unauthorized boot changes)',
    'Secure Boot enabled',
    'System in a physically secure location',
  ] },
  { id: 'sw', icon: '\u{1F4BE}', title: 'Software & Updates', items: [
    'All software up to date (browsers, PDF readers, etc.)',
    'Automatic updates enabled',
    'No pirated or unlicensed software',
    'Browser security settings configured (block pop-ups, safe browsing)',
    'Unnecessary services/software removed',
  ] },
  { id: 'data', icon: '\u{1F4BF}', title: 'Data Protection & Backup', items: [
    'Regular backups configured and tested',
    'Backups stored offsite or on a separate device',
    'Backup testing performed regularly',
    'Sensitive data encrypted',
  ] },
];

export function secTotalItems() {
  let n = 0;
  for (let i = 0; i < SEC_CATEGORIES.length; i++) n += SEC_CATEGORIES[i].items.length;
  return n;
}

export function secRiskLevel(passed, total) {
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  let level = 'HIGH RISK';
  let color = '#f87171';
  if (pct >= 80) { level = 'LOW RISK'; color = '#4ade80'; }
  else if (pct >= 50) { level = 'MEDIUM RISK'; color = '#fbbf24'; }
  return { pct: pct, level: level, color: color };
}

export const SEC_NEXT_STEPS = [
  'Address the highest-risk findings first.',
  'Apply the recommended security improvements.',
  'Re-check the system after changes to confirm the findings are resolved.',
  'Cover security best practices with the user where relevant.',
  'Keep updates and backups on a regular schedule.',
];

// ---------------------------------------------------------------------------
// Password Policy Generator data + engine (technician policy guidance).
//
// Converted from a source that produced a branded, customer-named
// "PASSWORD SECURITY POLICY" document with a Company/Organization Name field,
// an HR/legal "Policy Compliance" section (termination / legal action), a
// shop footer, a MASTER_HUB link, and a print button. The source has NO real
// password input and NO password generator; it is policy-generation only.
// Per the locked password/credential boundary: the named-company field is
// dropped (policy is generic), the HR/legal enforcement wording is replaced
// with neutral technician remediation guidance, and the output is reframed as
// an in-session "Technician Password Policy Summary". The prohibited-examples
// and passphrase illustration are guidance patterns, not credentials. This
// engine never accepts, generates, displays, or stores an actual credential.
// No customer record, certificate, quote, download, or persistence.
// ---------------------------------------------------------------------------
export const PP_MIN_LENGTHS = [
  { value: '8', label: '8 characters (minimum acceptable)' },
  { value: '10', label: '10 characters (recommended)' },
  { value: '12', label: '12 characters (strong)' },
  { value: '14', label: '14 characters (very strong)' },
  { value: '16', label: '16+ characters (maximum security)' },
];

export const PP_COMPLEXITY = [
  { value: 'basic', label: 'Basic (letters and numbers)' },
  { value: 'standard', label: 'Standard (letters, numbers, special characters)' },
  { value: 'advanced', label: 'Advanced (mixed case, numbers, symbols, no dictionary words)' },
];

export const PP_EXPIRATION = [
  { value: '30', label: '30 days (high security)' },
  { value: '60', label: '60 days (moderate security)' },
  { value: '90', label: '90 days (standard)' },
  { value: '180', label: '180 days (low-risk environments)' },
  { value: 'never', label: 'No expiration (not recommended)' },
];

export const PP_HISTORY = [
  { value: '3', label: 'Remember last 3 passwords' },
  { value: '5', label: 'Remember last 5 passwords' },
  { value: '10', label: 'Remember last 10 passwords' },
  { value: '24', label: 'Remember last 24 passwords (maximum)' },
];

export const PP_LOCKOUT_THRESHOLD = [
  { value: '3', label: '3 failed attempts' },
  { value: '5', label: '5 failed attempts' },
  { value: '10', label: '10 failed attempts' },
  { value: 'never', label: 'No lockout (not recommended)' },
];

export const PP_LOCKOUT_DURATION = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '60 minutes' },
  { value: 'manual', label: 'Manual unlock by administrator' },
];

export const PP_MFA = [
  { value: 'required', label: 'Required for all accounts' },
  { value: 'admin', label: 'Required for admin accounts' },
  { value: 'recommended', label: 'Recommended but optional' },
  { value: 'not_implemented', label: 'Not implemented' },
];

export const PP_SESSION_TIMEOUT = [
  { value: '5', label: '5 minutes (high security)' },
  { value: '15', label: '15 minutes (standard)' },
  { value: '30', label: '30 minutes (moderate)' },
  { value: '60', label: '60 minutes (low risk)' },
];

export const PP_STORAGE_POLICY = [
  { value: 'no_writing', label: 'Passwords must not be written down or stored in plain text' },
  { value: 'password_manager', label: 'Use of a password manager is required' },
  { value: 'encrypted_only', label: 'Passwords may only be stored in encrypted form' },
];

export const PP_PROHIBITED_TYPES = [
  'Personal information (names, birthdates, phone numbers)',
  'Sequential characters (abc, 123, qwerty)',
  'Repeated characters (aaa, 111)',
  'Previously used passwords (see password history)',
];

export const PP_COMPROMISE_STEPS = [
  'Change the affected password immediately.',
  'Report the incident to the responsible IT or security contact.',
  'Review recent account activity for unauthorised access.',
];

export const PP_BEST_PRACTICES = [
  'Use a password manager to generate and store complex passwords.',
  'Use a unique password for each system and account.',
  'Enable MFA wherever it is available.',
  'Prefer long passphrases over short complex strings.',
  'Never reuse work passwords on personal accounts.',
  'Be cautious of phishing attempts that request password information.',
];

export const PP_COMPLIANCE_NOTES = [
  'Accounts that do not meet these requirements should be flagged for remediation.',
  'Repeated non-compliance should be escalated according to the organisation\u0027s own internal policy.',
  'Re-check accounts after changes to confirm the requirements are met.',
];

function ppLabel(list, value) {
  for (let i = 0; i < list.length; i++) {
    if (list[i].value === value) return list[i].label;
  }
  return 'N/A';
}

export function getComplexityDescription(value) {
  if (value === 'basic') return 'Passwords must contain both letters and numbers.';
  if (value === 'advanced') return 'Passwords must contain uppercase and lowercase letters, numbers, and special characters, and must not contain dictionary words or common patterns.';
  return 'Passwords must contain uppercase letters, lowercase letters, numbers, and at least one special character (for example ! @ # $ % ^ & *).';
}

// Pure engine. Data-in/data-out. Produces policy-guidance text only. Never
// accepts, generates, displays, or stores an actual credential.
export function buildPasswordPolicy(inp) {
  const minLength = inp && inp.minLength ? inp.minLength : '12';
  const complexity = inp && inp.complexity ? inp.complexity : 'standard';
  const expiration = inp && inp.expiration ? inp.expiration : '90';
  const history = inp && inp.history ? inp.history : '5';
  const lockoutThreshold = inp && inp.lockoutThreshold ? inp.lockoutThreshold : '5';
  const lockoutDuration = inp && inp.lockoutDuration ? inp.lockoutDuration : '30';
  const mfa = inp && inp.mfa ? inp.mfa : 'admin';
  const sessionTimeout = inp && inp.sessionTimeout ? inp.sessionTimeout : '15';
  const storagePolicy = inp && inp.storagePolicy ? inp.storagePolicy : 'no_writing';

  let expirationText = '';
  if (expiration === 'never') {
    expirationText = 'Passwords do not expire (not recommended for security-sensitive environments).';
  } else {
    expirationText = 'Passwords should be changed every ' + expiration + ' days, with a reminder around 7 days before expiry.';
  }

  let lockoutText = '';
  if (lockoutThreshold === 'never') {
    lockoutText = 'No account lockout is enforced (not recommended).';
  } else {
    const durationText = lockoutDuration === 'manual' ? 'until an administrator unlocks the account' : 'for ' + lockoutDuration + ' minutes';
    lockoutText = 'After ' + lockoutThreshold + ' failed login attempts, the account is locked ' + durationText + '.';
  }

  const historyText = 'The last ' + history + ' passwords should be remembered so they cannot be reused at the next change.';
  const sessionText = 'Sessions should time out after ' + sessionTimeout + ' minutes of inactivity, requiring re-authentication.';

  return {
    minLength: minLength,
    complexityLabel: ppLabel(PP_COMPLEXITY, complexity),
    complexityDesc: getComplexityDescription(complexity),
    expirationText: expirationText,
    historyText: historyText,
    lockoutText: lockoutText,
    mfaLabel: ppLabel(PP_MFA, mfa),
    sessionText: sessionText,
    storageLabel: ppLabel(PP_STORAGE_POLICY, storagePolicy),
  };
}

// ---------------------------------------------------------------------------
// Technician Tool Index data (reference-only external tool list).
//
// Converted from a source USB Tech Tools Launcher that used a USB drive-letter
// input, per-tool copyCommand() buttons building local paths
// (X:\\USB Tech\\Category\\Tool.exe) and a "start" command written to a .bat
// file / clipboard, plus instructions to paste and run the command so the tool
// "launches automatically". ALL launcher, USB, local-path, batch-file, .exe /
// .msi filename, clipboard-command, and folder behaviour is removed. Only the
// tool name, category, purpose note, and a reference status survive. VISINVOKE
// hosts, bundles, downloads, launches, and executes NOTHING here. No official
// vendor URLs existed in the source (only local USB paths), so none are
// included and none are invented. Commercial or licensed tools are marked
// "licence"; everything else defaults to "official" (obtain from the vendor).
// The source "Windows Key Finders" category is retained as neutral
// "Product Key Recovery" entries (recovering a system's OWN installed key for
// reinstallation, not generating keys) under Licence Review Required. No
// keygen / crack / serial / torrent / warez / patched entries were present.
// ---------------------------------------------------------------------------
export const TTI_STATUS = {
  reference: { label: 'Reference Only', color: '#38bdf8' },
  official: { label: 'Official Link Required', color: '#fbbf24' },
  licence: { label: 'Licence Review Required', color: '#fb923c' },
  blocked: { label: 'Blocked', color: '#f87171' },
};

export function getTtiStatus(value) {
  if (value === 'reference') return TTI_STATUS.reference;
  if (value === 'licence') return TTI_STATUS.licence;
  if (value === 'blocked') return TTI_STATUS.blocked;
  return TTI_STATUS.official;
}

export const TTI_CATEGORIES = [
  { id: 'battery', icon: '\u{1F50B}', title: 'Battery Tester', tools: [
    { name: 'Battery Eater Pro', status: 'official', note: 'Laptop battery load testing and runtime measurement.' },
    { name: 'Battery Gauge', status: 'official', note: 'Battery status and health readout.' },
    { name: 'BatteryBar', status: 'official', note: 'Taskbar battery runtime estimator.' },
    { name: 'BatteryCare', status: 'official', note: 'Battery monitoring and maintenance utility.' },
  ] },
  { id: 'benchmark', icon: '\u{1F4CA}', title: 'Benchmark Tools', tools: [
    { name: 'Cinebench R15', status: 'official', note: 'CPU and GPU rendering benchmark.' },
    { name: 'CPU Benchmark (CPUFreeBenchMark)', status: 'official', note: 'CPU performance benchmark.' },
    { name: 'NovaBench', status: 'official', note: 'Overall system benchmark.' },
    { name: 'UserBenchmark', status: 'official', note: 'Component comparison benchmark; results interpretation varies.' },
    { name: 'Unigine Valley', status: 'official', note: 'GPU stress test and benchmark.' },
  ] },
  { id: 'browser', icon: '\u{1F310}', title: 'Browser Installers', tools: [
    { name: 'Google Chrome', status: 'official', note: 'Web browser.' },
    { name: 'Mozilla Firefox', status: 'official', note: 'Web browser.' },
    { name: 'Opera Browser', status: 'official', note: 'Web browser.' },
    { name: 'Betterbird', status: 'official', note: 'Thunderbird-based email client.' },
  ] },
  { id: 'cd', icon: '\u{1F4BF}', title: 'Virtual CD/DVD', tools: [
    { name: 'Daemon Tools Lite', status: 'licence', note: 'Disc image mounting; free build carries bundled offers, review before use.' },
    { name: 'Virtual CloneDrive', status: 'official', note: 'Free disc image mounting.' },
    { name: 'WinCDEmu', status: 'official', note: 'Open-source disc image mounting.' },
  ] },
  { id: 'diagnostic', icon: '\u{1F50D}', title: 'Diagnostic Tools', tools: [
    { name: 'AIDA64 Extreme', status: 'licence', note: 'Hardware diagnostics and benchmarking; commercial licence.' },
    { name: 'HWiNFO64', status: 'official', note: 'Hardware information and sensor monitoring.' },
    { name: 'Open Hardware Monitor', status: 'official', note: 'Open-source sensor monitoring.' },
    { name: 'IObit ToolBox', status: 'licence', note: 'Portable admin diagnostic toolset; review bundled components.' },
    { name: 'Burn In Test Pro', status: 'licence', note: 'Commercial stability and burn-in testing.' },
    { name: 'DiskBench', status: 'official', note: 'Disk throughput testing.' },
  ] },
  { id: 'hdd', icon: '\u{1F4BE}', title: 'Hard Drive Tools', tools: [
    { name: 'DriveSnapshot', status: 'licence', note: 'Disk imaging and backup; commercial licence.' },
    { name: 'Samsung Magician', status: 'official', note: 'Samsung SSD management (vendor utility).' },
    { name: 'Samsung Data Migration', status: 'official', note: 'Samsung SSD cloning (vendor utility).' },
  ] },
  { id: 'network', icon: '\u{1F5A7}', title: 'Networking Tools', tools: [
    { name: 'Advanced IP Scanner', status: 'official', note: 'Network device scanner.' },
    { name: 'FileZilla', status: 'official', note: 'FTP client; use the clean official build without bundled offers.' },
    { name: 'LAN Speed Test', status: 'licence', note: 'LAN throughput testing; commercial licence.' },
    { name: 'Angry IP Scanner', status: 'official', note: 'Open-source IP and port scanner.' },
  ] },
  { id: 'office', icon: '\u{1F4C4}', title: 'Office Software', tools: [
    { name: 'Apache OpenOffice', status: 'official', note: 'Open-source office suite.' },
    { name: 'Microsoft Office Setup', status: 'licence', note: 'Microsoft Office deployment installer; requires a valid licence.' },
  ] },
  { id: 'utilities', icon: '\u{1F6E0}', title: 'Utilities & Setup', tools: [
    { name: 'CCleaner', status: 'official', note: 'System cleanup; use the official build and review bundled offers.' },
    { name: 'WinRAR', status: 'licence', note: 'Archiver; commercial licence after the trial period.' },
    { name: 'VLC Media Player', status: 'official', note: 'Open-source media player.' },
    { name: 'MalwareBytes', status: 'official', note: 'Anti-malware scanner.' },
    { name: 'Classic Shell', status: 'official', note: 'Start-menu replacement; successor project is Open-Shell.' },
    { name: 'Everything Search', status: 'official', note: 'Instant filename search index.' },
  ] },
  { id: 'testing', icon: '\u{1F9EA}', title: 'Testing & Monitoring', tools: [
    { name: 'CrystalDiskInfo', status: 'official', note: 'Drive SMART health monitoring.' },
    { name: 'CrystalDiskMark', status: 'official', note: 'Drive speed benchmark.' },
    { name: 'CPU-Z', status: 'official', note: 'CPU and mainboard information.' },
    { name: 'GPU-Z', status: 'official', note: 'GPU information and monitoring.' },
    { name: 'HWMonitor', status: 'official', note: 'Hardware sensor monitoring.' },
    { name: 'Core Temp', status: 'official', note: 'CPU temperature monitoring.' },
    { name: 'Display Driver Uninstaller (DDU)', status: 'official', note: 'Clean GPU driver removal.' },
    { name: 'Intel Burn Test', status: 'official', note: 'Aggressive CPU stress testing; use with care.' },
    { name: 'HWiNFO64 (Testing)', status: 'official', note: 'Hardware information and sensors.' },
  ] },
  { id: 'virus', icon: '\u{1F9A0}', title: 'Virus & Malware Removal', tools: [
    { name: 'MalwareBytes', status: 'official', note: 'Anti-malware scanner.' },
    { name: 'AdwCleaner', status: 'official', note: 'Adware and PUP removal.' },
  ] },
  { id: 'repair', icon: '\u{1F527}', title: 'Windows Repair', tools: [
    { name: 'Tweaking.com Windows Repair', status: 'official', note: 'Windows repair and reset utility.' },
  ] },
  { id: 'keyrecovery', icon: '\u{1F511}', title: 'Product Key Recovery', tools: [
    { name: 'ProduKey', status: 'licence', note: 'Recovers a system\u0027s own already-installed Windows/Office product key for legitimate reinstallation. Not a key generator. May be flagged by antivirus; use only on owner-authorised systems.' },
    { name: 'Key Finder', status: 'licence', note: 'Recovers an existing installed product key for reinstallation. Not a key generator; use only on owner-authorised systems.' },
  ] },
];

export function ttiToolCount() {
  let n = 0;
  for (let i = 0; i < TTI_CATEGORIES.length; i++) n += TTI_CATEGORIES[i].tools.length;
  return n;
}

// ===========================================================================
// External Tool Downloads catalogue.
//
// The single, unified external-tool catalogue for the VISINVOKE Tech Hub page.
// It replaces the former Technician Tool Index (TTI) reference card as the one
// external-tool area on the page.
//
// Model: every card is visible to every signed-in VISINVOKE user so general
// users can see what technician access offers. The working supplier link is
// gated by Technician Access (the technician_access entitlement, resolved by
// getTechnicianAccess). Technician accounts open the official supplier, vendor
// or project page in a new tab through the VISINVOKE Desktop Command Center.
// General users see the same card with a disabled Technician Access Required
// button and never receive the clickable official URL.
//
// Legal model: official supplier/vendor/project links only. VISINVOKE does not
// host, mirror, bundle, download, install, run, proxy or track these tools. No
// EXE/MSI/ZIP/RAR/7Z/REG/driver hotlinks. No keygens, cracks, serials,
// activators or licence-bypass tools. Only the approved clean list below.
//
// Card data shape (fixed zones are rendered by ExternalToolCard):
//   id, name, section, category, icon, description, technicianUse,
//   licenceBadge, safetyBadge, accessRequired, technicianActionLabel,
//   lockedActionLabel, officialUrl, colourTheme
// ===========================================================================

// Section-level copy shown above the External Tool Downloads catalogue.
export const EXTERNAL_TOOLS_SECTION = {
  title: 'External Tool Downloads',
  helper: 'Official supplier links for commonly used technician tools. VISINVOKE does not host, mirror, download, install or run these third-party tools. Approved Technician Access accounts can open the supplier pages from VISINVOKE Tech Hub and the VISINVOKE Desktop Command Center.',
  purposeNote: 'These links are not secret tools. They are organised for speed and consistency, so technicians can reach the correct official supplier pages quickly while working on client systems.',
  lockedHelper: 'Technician Access Required: general VISINVOKE users can view this catalogue, but download links are unlocked only for approved technician accounts.',
  accessNotice: 'External tool download links are available to approved Technician Access accounts through VISINVOKE Tech Hub and the VISINVOKE Desktop Command Center. General users can view the catalogue, but working supplier links require technician access.',
};

// Default action labels for the External Tool Downloads cards.
export const EXTERNAL_TOOL_LOCKED_LABEL = 'Technician Access Required';

// Resolve the External Tool Downloads action state from a card and the viewer
// Technician Access. Returns display metadata only; it never opens anything.
// When unlocked is true the caller may open card.officialUrl in a new tab.
export function getExternalToolAccessState(tool, hasAccess) {
  if (!hasAccess) {
    return {
      unlocked: false,
      label: (tool && tool.lockedActionLabel) ? tool.lockedActionLabel : EXTERNAL_TOOL_LOCKED_LABEL,
      color: '#fbbf24',
    };
  }
  return {
    unlocked: true,
    label: (tool && tool.technicianActionLabel) ? tool.technicianActionLabel : 'Open Official Download Page',
    color: '#34d399',
  };
}

// The 11 approved external tool groups, each with its own colour theme.
export const EXTERNAL_TOOL_GROUPS = [
  {
    id: 'diagnostics',
    title: 'Diagnostics & System Information',
    icon: '\u{1F50D}',
    accent: '#3b82f6',
    helperText: 'Use these tools to identify hardware, check live sensor readings, confirm temperatures, inspect system details and investigate crash symptoms.',
    tools: [
      { id: 'hwinfo', name: 'HWiNFO', section: 'Diagnostics & System Information', category: 'Hardware diagnostics', icon: '\u{1F5A5}', description: 'Shows detailed information about the CPU, motherboard, memory, storage, graphics, sensors and temperatures.', technicianUse: 'Use it when you need a full hardware report or live readings such as temperatures, voltages and fan speeds.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Commercial Use Terms', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.hwinfo.com/download/', colourTheme: '#3b82f6' },
      { id: 'open-hardware-monitor', name: 'Open Hardware Monitor', section: 'Diagnostics & System Information', category: 'Sensor monitoring', icon: '\u{1F321}', description: 'Monitors temperatures, fan speeds, voltages, clocks and system load.', technicianUse: 'Use it to quickly check whether a system is overheating or behaving abnormally under load.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Admin May Be Required', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://openhardwaremonitor.org/downloads/', colourTheme: '#3b82f6' },
      { id: 'cpu-z', name: 'CPU-Z', section: 'Diagnostics & System Information', category: 'CPU and platform information', icon: '\u{1F9E0}', description: 'Shows CPU model, motherboard, BIOS, memory and basic system platform details.', technicianUse: 'Use it to quickly confirm what hardware is inside a PC without opening the case.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.cpuid.com/softwares/cpu-z.html', colourTheme: '#3b82f6' },
      { id: 'hwmonitor', name: 'HWMonitor', section: 'Diagnostics & System Information', category: 'Temperature and sensor monitoring', icon: '\u{1F4CF}', description: 'Displays temperatures, voltages, fan speeds, utilisation and other hardware sensor readings.', technicianUse: 'Use it to check thermal problems, fan behaviour and sensor values during troubleshooting.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.cpuid.com/softwares/hwmonitor.html', colourTheme: '#3b82f6' },
      { id: 'gpu-z', name: 'GPU-Z', section: 'Diagnostics & System Information', category: 'Graphics diagnostics', icon: '\u{1F3AE}', description: 'Shows graphics card model, memory, BIOS, clocks, sensors and driver details.', technicianUse: 'Use it when checking graphics cards, confirming GPU specifications or investigating display issues.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.techpowerup.com/gpuz/', colourTheme: '#3b82f6' },
      { id: 'bluescreenview', name: 'BlueScreenView', section: 'Diagnostics & System Information', category: 'Crash analysis', icon: '\u{1F6D1}', description: 'Reads Windows minidump files and shows details from blue screen crashes.', technicianUse: 'Use it to identify likely drivers or modules involved in BSOD crashes before deeper debugging.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Advanced Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.nirsoft.net/utils/blue_screen_view.html', colourTheme: '#3b82f6' },
      { id: 'siv', name: 'SIV', section: 'Diagnostics & System Information', category: 'Advanced system information', icon: '\u{1F4D1}', description: 'Provides detailed system, hardware, network and sensor information for advanced diagnostics.', technicianUse: 'Use it when you need deeper technical details than normal Windows tools provide.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Advanced Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://rh-software.com/', colourTheme: '#3b82f6' },
      { id: 'core-temp', name: 'Core Temp', section: 'Diagnostics & System Information', category: 'CPU temperature monitor', icon: '\u{1F525}', description: 'Shows CPU core temperatures and processor load information.', technicianUse: 'Use it for quick temperature checks during overheating or stability investigations.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.alcpu.com/CoreTemp/', colourTheme: '#3b82f6' },
    ],
  },
  {
    id: 'storage',
    title: 'Drive, SSD & Storage',
    icon: '\u{1F4BD}',
    accent: '#06b6d4',
    helperText: 'Use these tools to check drive health, inspect S.M.A.R.T. data, test storage speed and access vendor SSD support tools.',
    tools: [
      { id: 'crystaldiskinfo', name: 'CrystalDiskInfo', section: 'Drive, SSD & Storage', category: 'Drive health', icon: '\u{1F4BF}', description: 'Checks HDD, SSD and NVMe health using S.M.A.R.T. data, temperature readings and drive condition indicators.', technicianUse: 'Use it as a first check when a customer reports slow performance, freezing, drive noise or possible storage failure.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Admin May Be Required', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://crystalmark.info/en/software/crystaldiskinfo/', colourTheme: '#06b6d4' },
      { id: 'crystaldiskmark', name: 'CrystalDiskMark', section: 'Drive, SSD & Storage', category: 'Storage benchmark', icon: '\u{1F4CA}', description: 'Benchmarks read and write performance for SSDs, hard drives, USB drives and external storage.', technicianUse: 'Use it to confirm whether a drive is performing normally after upgrade, cloning or repair work.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://crystalmark.info/en/software/crystaldiskmark/', colourTheme: '#06b6d4' },
      { id: 'samsung-magician', name: 'Samsung Magician', section: 'Drive, SSD & Storage', category: 'Samsung SSD utility', icon: '\u{1F9F0}', description: 'Samsung SSD management tool for drive health, firmware, diagnostics and performance features.', technicianUse: 'Use it for Samsung SSD checks, firmware updates, optimisation and migration support.', licenceBadge: 'Vendor Tool / Official Link Only', safetyBadge: 'Vendor Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://semiconductor.samsung.com/consumer-storage/magician/', colourTheme: '#06b6d4' },
      { id: 'samsung-ssd-tools', name: 'Samsung SSD Tools', section: 'Drive, SSD & Storage', category: 'Samsung SSD support', icon: '\u{1F527}', description: 'Official Samsung support tools for SSD migration, portable SSD software and related storage utilities.', technicianUse: 'Use this when working specifically with Samsung SSDs or when migration support is needed.', licenceBadge: 'Vendor Tool / Official Link Only', safetyBadge: 'Vendor Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://semiconductor.samsung.com/consumer-storage/support/tools/', colourTheme: '#06b6d4' },
      { id: 'hddscan', name: 'HDDScan', section: 'Drive, SSD & Storage', category: 'Drive diagnostics', icon: '\u{1F50E}', description: 'Tests storage devices and can help identify drive surface, health or access problems.', technicianUse: 'Use it when a deeper storage check is needed beyond a quick S.M.A.R.T. health screen.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Advanced Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://hddscan.com/', colourTheme: '#06b6d4' },
      { id: 'diskbench', name: 'DiskBench', section: 'Drive, SSD & Storage', category: 'Disk performance test', icon: '\u{23F1}', description: 'A simple utility for testing practical disk copy and storage performance.', technicianUse: 'Use it for a simple real-world style storage speed check.', licenceBadge: 'Freeware / Source Check', safetyBadge: 'Reference Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.nodesoft.com/diskbench', colourTheme: '#06b6d4' },
    ],
  },
  {
    id: 'drivers',
    title: 'Drivers & Device Support',
    icon: '\u{1F50C}',
    accent: '#6366f1',
    helperText: 'Use these tools for driver cleanup, vendor driver downloads and device support. New technicians should be careful with driver removal tools.',
    tools: [
      { id: 'ddu', name: 'Display Driver Uninstaller', section: 'Drivers & Device Support', category: 'Graphics driver cleanup', icon: '\u{1F5D1}', description: 'Removes NVIDIA, AMD and Intel display drivers when normal uninstall does not clean them properly.', technicianUse: 'Use it when fixing corrupted GPU drivers, failed driver upgrades or recurring display-driver issues.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Advanced Tool / Use With Caution', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.wagnardsoft.com/display-driver-uninstaller-ddu', colourTheme: '#6366f1' },
      { id: 'intel-ethernet-pack', name: 'Intel Ethernet Adapter Complete Driver Pack', section: 'Drivers & Device Support', category: 'Network drivers', icon: '\u{1F310}', description: 'Official Intel Ethernet driver package for supported Intel network adapters.', technicianUse: 'Use it when Windows does not have the correct Intel LAN driver or a clean network driver install is needed.', licenceBadge: 'Vendor Tool / Official Link Only', safetyBadge: 'Vendor Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.intel.com/content/www/us/en/download/15084/intel-ethernet-adapter-complete-driver-pack.html', colourTheme: '#6366f1' },
    ],
  },
  {
    id: 'networking',
    title: 'Networking',
    icon: '\u{1F310}',
    accent: '#34d399',
    helperText: 'Use these tools to discover local network devices, test network speed and transfer files to servers or hosting accounts.',
    tools: [
      { id: 'filezilla', name: 'FileZilla Client', section: 'Networking', category: 'FTP and SFTP', icon: '\u{1F4C1}', description: 'FTP, FTPS and SFTP client for transferring files to websites, hosting accounts and servers.', technicianUse: 'Use it for website file transfers, hosting work and server file access.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://filezilla-project.org/', colourTheme: '#34d399' },
      { id: 'advanced-ip-scanner', name: 'Advanced IP Scanner', section: 'Networking', category: 'Network scanner', icon: '\u{1F4E1}', description: 'Scans a local network and shows connected devices, IP addresses and available network resources.', technicianUse: 'Use it to find devices on a customer network or identify IP addresses during setup work.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.advanced-ip-scanner.com/download/', colourTheme: '#34d399' },
      { id: 'angry-ip-scanner', name: 'Angry IP Scanner', section: 'Networking', category: 'Network scanner', icon: '\u{1F5FA}', description: 'Open-source IP scanner for scanning IP ranges and checking live hosts.', technicianUse: 'Use it to quickly see which devices respond on a network range.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://angryip.org/download/', colourTheme: '#34d399' },
      { id: 'lan-speed-test', name: 'LAN Speed Test', section: 'Networking', category: 'Network speed test', icon: '\u{1F4C8}', description: 'Tests local network speed between computers, servers or shared folders.', technicianUse: 'Use it when checking local network performance, cabling, switches or shared storage speed.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://totusoft.com/lanspeed', colourTheme: '#34d399' },
    ],
  },
  {
    id: 'security',
    title: 'Security & Malware Cleanup',
    icon: '\u{1F6E1}',
    accent: '#dc2626',
    helperText: 'Use these tools for malware, adware and unwanted software cleanup. Always download security tools from the official vendor.',
    tools: [
      { id: 'malwarebytes-free', name: 'Malwarebytes Free', section: 'Security & Malware Cleanup', category: 'Malware removal', icon: '\u{1F9F9}', description: 'Scans for and removes malware, ransomware, spyware, adware and unwanted threats.', technicianUse: 'Use it as a common malware cleanup tool after checking that the customer has approved the scan.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Security Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.malwarebytes.com/mwb-download', colourTheme: '#dc2626' },
      { id: 'adwcleaner', name: 'Malwarebytes AdwCleaner', section: 'Security & Malware Cleanup', category: 'Adware cleanup', icon: '\u{1F4A5}', description: 'Removes adware, potentially unwanted programs, browser hijackers and unwanted toolbars.', technicianUse: 'Use it when browsers are full of popups, changed search engines or unwanted extensions.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Security Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.malwarebytes.com/adwcleaner', colourTheme: '#dc2626' },
    ],
  },
  {
    id: 'browsers-email',
    title: 'Browsers & Email',
    icon: '\u{1F9ED}',
    accent: '#a855f7',
    helperText: 'Use these links for clean browser installers and email-client support from official suppliers.',
    tools: [
      { id: 'google-chrome', name: 'Google Chrome', section: 'Browsers & Email', category: 'Browser', icon: '\u{1F30D}', description: 'Google browser for general browsing, customer installs and compatibility testing.', technicianUse: 'Use it when reinstalling Chrome, testing browser issues or setting up a clean browser profile.', licenceBadge: 'Vendor Tool / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.google.com/chrome/', colourTheme: '#a855f7' },
      { id: 'mozilla-firefox', name: 'Mozilla Firefox', section: 'Browsers & Email', category: 'Browser', icon: '\u{1F98A}', description: 'Mozilla browser for general browsing, testing and customer installs.', technicianUse: 'Use it as a clean alternative browser or when checking whether a problem is browser-specific.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.mozilla.org/firefox/', colourTheme: '#a855f7' },
      { id: 'opera', name: 'Opera', section: 'Browsers & Email', category: 'Browser', icon: '\u{1F3AD}', description: 'Alternative browser used for browser testing and customer preference installs.', technicianUse: 'Use it when a customer prefers Opera or when comparing browser behaviour.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.opera.com/download', colourTheme: '#a855f7' },
      { id: 'betterbird', name: 'Betterbird', section: 'Browsers & Email', category: 'Email client', icon: '\u{1F4E7}', description: 'Thunderbird-based email client with additional fixes and features.', technicianUse: 'Use it when supporting desktop email workflows or testing Thunderbird-style mail setup.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.betterbird.eu/downloads/', colourTheme: '#a855f7' },
    ],
  },
  {
    id: 'iso-tools',
    title: 'Virtual CD & ISO Tools',
    icon: '\u{1F4BF}',
    accent: '#f59e0b',
    helperText: 'Use these tools to mount ISO and disc image files without burning physical discs.',
    tools: [
      { id: 'wincdemu', name: 'WinCDEmu', section: 'Virtual CD & ISO Tools', category: 'ISO mounting', icon: '\u{1F4C0}', description: 'Open-source ISO and disc image mounting utility for Windows.', technicianUse: 'Use it to mount ISO files during software installs, recovery work or image testing.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Driver Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://wincdemu.sysprogs.org/download/', colourTheme: '#f59e0b' },
      { id: 'virtual-clonedrive', name: 'Virtual CloneDrive', section: 'Virtual CD & ISO Tools', category: 'ISO mounting', icon: '\u{1F4BD}', description: 'Freeware image mounting tool for ISO and other virtual disc image formats.', technicianUse: 'Use it when a simple virtual disc drive is needed for ISO-based installers or images.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Driver Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.elby.ch/en/products/vcd.html', colourTheme: '#f59e0b' },
    ],
  },
  {
    id: 'office-media',
    title: 'Office, Media & General Utilities',
    icon: '\u{1F9F0}',
    accent: '#22c55e',
    helperText: 'Use these tools for common technician tasks such as opening documents, testing media, finding files, managing archives and unlocking stuck files.',
    tools: [
      { id: 'apache-openoffice', name: 'Apache OpenOffice', section: 'Office, Media & General Utilities', category: 'Office suite', icon: '\u{1F4C4}', description: 'Free office suite for opening and editing documents, spreadsheets and presentations.', technicianUse: 'Use it when a customer needs a free office suite or a document needs to be opened without Microsoft Office.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.openoffice.org/download/', colourTheme: '#22c55e' },
      { id: 'vlc', name: 'VLC Media Player', section: 'Office, Media & General Utilities', category: 'Media playback', icon: '\u{1F3AC}', description: 'Open-source media player for testing video, audio, DVDs, streams and codec playback.', technicianUse: 'Use it when checking whether a media playback issue is caused by Windows apps or missing codecs.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.videolan.org/vlc/download-windows.html', colourTheme: '#22c55e' },
      { id: 'everything', name: 'Everything', section: 'Office, Media & General Utilities', category: 'File search', icon: '\u{1F50D}', description: 'Fast Windows filename search utility for finding files and folders quickly.', technicianUse: 'Use it when a customer cannot find files or when you need to locate files across a drive quickly.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.voidtools.com/downloads/', colourTheme: '#22c55e' },
      { id: 'winrar', name: 'WinRAR', section: 'Office, Media & General Utilities', category: 'Archive utility', icon: '\u{1F5DC}', description: 'Archive utility for opening and creating RAR, ZIP and other compressed archive files.', technicianUse: 'Use it when customers provide compressed files or when older archive formats need to be opened.', licenceBadge: 'Commercial / Trialware / Official Link Only', safetyBadge: 'Commercial Tool', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.rarlab.com/download.htm', colourTheme: '#22c55e' },
      { id: 'lockhunter', name: 'LockHunter', section: 'Office, Media & General Utilities', category: 'File unlocker', icon: '\u{1F513}', description: 'Finds what process is locking a file and helps unlock, rename or delete stuck files.', technicianUse: 'Use it when Windows says a file is in use and normal close or restart steps are not enough.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Use With Caution', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://lockhunter.com/download.htm', colourTheme: '#22c55e' },
    ],
  },
  {
    id: 'benchmarking',
    title: 'Benchmarking & Stress Testing',
    icon: '\u{1F4CA}',
    accent: '#f97316',
    helperText: 'Use these tools to measure performance or test system stability. New technicians should understand that stress testing can expose heat, power or hardware problems.',
    tools: [
      { id: 'novabench', name: 'Novabench', section: 'Benchmarking & Stress Testing', category: 'General benchmark', icon: '\u{1F4C8}', description: 'Benchmarks CPU, GPU, memory and storage performance.', technicianUse: 'Use it for a quick before-and-after performance check after upgrades or repairs.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Official Link Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://novabench.com/', colourTheme: '#f97316' },
      { id: 'unigine-valley', name: 'Unigine Valley', section: 'Benchmarking & Stress Testing', category: 'GPU benchmark', icon: '\u{1F3D4}', description: 'Graphics benchmark and stress-test scene for checking GPU performance and stability.', technicianUse: 'Use it when testing graphics cards, cooling or system stability under GPU load.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Stress Test', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://benchmark.unigine.com/valley', colourTheme: '#f97316' },
      { id: 'userbenchmark', name: 'UserBenchmark', section: 'Benchmarking & Stress Testing', category: 'Reference benchmark', icon: '\u{1F4CB}', description: 'Quick benchmark and comparison tool for informal system performance checks.', technicianUse: 'Use it only as a rough reference, not as the final authority on system quality.', licenceBadge: 'Reference Only', safetyBadge: 'Informal Result', accessRequired: 'technician', technicianActionLabel: 'Open Vendor Website', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.userbenchmark.com/', colourTheme: '#f97316' },
      { id: 'passmark-burnintest', name: 'PassMark BurnInTest', section: 'Benchmarking & Stress Testing', category: 'Commercial stress test', icon: '\u{1F6A6}', description: 'Commercial tool for stress-testing system components under load.', technicianUse: 'Use it when a licensed stability test is needed for CPU, memory, disk, graphics or system validation.', licenceBadge: 'Commercial / Official Link Only', safetyBadge: 'Licence Required', accessRequired: 'technician', technicianActionLabel: 'Open Vendor Website', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.passmark.com/products/burnintest/', colourTheme: '#f97316' },
      { id: 'aida64', name: 'AIDA64', section: 'Benchmarking & Stress Testing', category: 'Commercial diagnostics', icon: '\u{1F9EA}', description: 'Commercial system diagnostics and stress-testing suite.', technicianUse: 'Use it when licensed advanced hardware diagnostics, reports or stability testing are required.', licenceBadge: 'Commercial / Official Link Only', safetyBadge: 'Licence Required', accessRequired: 'technician', technicianActionLabel: 'Open Vendor Website', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.aida64.com/downloads', colourTheme: '#f97316' },
      { id: 'compubench', name: 'CompuBench', section: 'Benchmarking & Stress Testing', category: 'Compute benchmark', icon: '\u{1F5A9}', description: 'Benchmark tool for testing compute and graphics performance.', technicianUse: 'Use it when checking GPU or compute performance in a benchmark workflow.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Reference Only', accessRequired: 'technician', technicianActionLabel: 'Open Vendor Website', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://compubench.com/', colourTheme: '#f97316' },
    ],
  },
  {
    id: 'battery',
    title: 'Battery Testing',
    icon: '\u{1F50B}',
    accent: '#ca8a04',
    helperText: 'Use these tools to check battery status, battery behaviour and discharge performance on laptops. Some older battery tools should be marked as reference only.',
    tools: [
      { id: 'batterybar', name: 'BatteryBar', section: 'Battery Testing', category: 'Battery monitor', icon: '\u{1F50B}', description: 'Shows battery status, wear, runtime estimates and charge information.', technicianUse: 'Use it for quick laptop battery checks and customer battery-health explanations.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Source Check', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://batterybarpro.com/basic.php', colourTheme: '#ca8a04' },
      { id: 'batterycare', name: 'BatteryCare', section: 'Battery Testing', category: 'Battery monitor', icon: '\u{1FAAB}', description: 'Laptop battery monitoring utility for battery status and usage information.', technicianUse: 'Use it as a simple battery health and usage reference tool.', licenceBadge: 'Freeware / Official Link Only', safetyBadge: 'Source Check', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://batterycare.net/en/download.html', colourTheme: '#ca8a04' },
      { id: 'battery-eater-pro', name: 'Battery Eater Pro', section: 'Battery Testing', category: 'Battery discharge test', icon: '\u{26A1}', description: 'Older battery benchmark used to test laptop battery discharge time under load.', technicianUse: 'Use only as a historical or reference battery test option if the official source is still acceptable.', licenceBadge: 'Reference Only', safetyBadge: 'Old Tool', accessRequired: 'technician', technicianActionLabel: 'Open Vendor Website', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://batteryeater.com/', colourTheme: '#ca8a04' },
    ],
  },
  {
    id: 'windows-repair',
    title: 'Windows Repair & Advanced Tools',
    icon: '\u{1F527}',
    accent: '#f43f5e',
    helperText: 'Use these tools only when normal repair steps are not enough. New technicians should read the notes first because repair tools can change services, permissions or system settings.',
    tools: [
      { id: 'tweaking-windows-repair', name: 'Tweaking.com Windows Repair', section: 'Windows Repair & Advanced Tools', category: 'Windows repair', icon: '\u{1F6E0}', description: 'Advanced Windows repair utility for permissions, Windows Update, services, firewall, WMI and common repair workflows.', technicianUse: 'Use it only after basic troubleshooting and backups, especially when malware cleanup or Windows corruption has damaged services.', licenceBadge: 'Freeware / Commercial / Official Link Only', safetyBadge: 'Advanced Tool / Use With Caution', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.tweaking.com/content/page/windows_repair_all_in_one.html', colourTheme: '#f43f5e' },
      { id: 'servicesrepair', name: 'ServicesRepair', section: 'Windows Repair & Advanced Tools', category: 'Service repair', icon: '\u{2699}', description: 'Utility intended to repair or restore Windows service-related problems.', technicianUse: 'Use only when the official source is confirmed and the service issue is clearly understood.', licenceBadge: 'Source Check Required', safetyBadge: 'Reference Only', accessRequired: 'technician', technicianActionLabel: 'Open Official Documentation', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://www.eightforums.com/threads/services-restore-default-services-in-windows-8.9413/', colourTheme: '#f43f5e' },
      { id: 'open-shell', name: 'Open-Shell', section: 'Windows Repair & Advanced Tools', category: 'Windows shell utility', icon: '\u{1F5A5}', description: 'Open-source continuation of the classic Start menu and shell customisation utility.', technicianUse: 'Use it only where a customer specifically wants a classic-style Windows Start menu workflow.', licenceBadge: 'Open Source / Official Link Only', safetyBadge: 'Optional Utility', accessRequired: 'technician', technicianActionLabel: 'Open Official Download Page', lockedActionLabel: 'Technician Access Required', officialUrl: 'https://github.com/Open-Shell/Open-Shell-Menu', colourTheme: '#f43f5e' },
    ],
  },
];

// Flat count of external tool cards, for summary display.
export function externalToolCount() {
  let n = 0;
  for (let i = 0; i < EXTERNAL_TOOL_GROUPS.length; i++) n += EXTERNAL_TOOL_GROUPS[i].tools.length;
  return n;
}

// ---------------------------------------------------------------------------
// Help group content (static VISINVOKE-native help pages).
//
// The three source files (QUICK_START.html, USER_GUIDE.html, README.html) were
// standalone-package documentation: extract files, open MASTER_HUB.html,
// bookmark it, work offline, run a USB tool launcher from X:\\USB Tech\\ with a
// .bat file, print customer reports, plus a Service Package Builder / Contract
// Calculator, shop branding, and stale tool counts. None of that is preserved.
// This content is a rewrite around the correct VISINVOKE account model: Tech
// Hub runs only through a signed-in VISINVOKE account with Technician Access;
// tools run in-session; on a client PC the technician signs in, works, and
// signs out; the client PC is never attached to the technician account; free
// utilities are free in price, not anonymous. No offline, USB, local-file,
// launcher, download, or persistence wording.
// ---------------------------------------------------------------------------
export const QUICK_START_STEPS = [
  { title: 'Open VISINVOKE', text: 'Open the VISINVOKE Desktop App on the working PC, or sign in to the VISINVOKE portal in a browser.' },
  { title: 'Sign in', text: 'Sign in to your VISINVOKE account. Tech Hub runs only through VISINVOKE, so a signed-in account is required.' },
  { title: 'Technician Access', text: 'Technician Access unlocks Tech Hub. If Tech Hub is not visible, your account does not yet carry Technician Access.' },
  { title: 'Open Tech Hub', text: 'Open Tech Hub from the Utilities area of VISINVOKE.' },
  { title: 'Choose a tool group', text: 'Pick one of the six tool groups, then open the tool you need.' },
  { title: 'Work in-session', text: 'Enter details and generate summaries in-session. Nothing is saved unless a future saved workflow is explicitly added.' },
  { title: 'Sign out on client PCs', text: 'When you finish on a client PC, sign out of VISINVOKE. The client PC is not attached to your account.' },
];

export const QUICK_START_NOTES = [
  'Tech Hub is part of VISINVOKE, not a standalone, offline, or USB toolkit.',
  'There are no files to extract, bookmark, or run locally; everything runs inside VISINVOKE.',
  'Free VISINVOKE utilities are free in price, not anonymous; a client can later create their own VISINVOKE account.',
];

export const USER_GUIDE_GROUPS = [
  { title: 'Diagnostic & Troubleshooting', text: 'Guided problem trees, common-fix and error-code references, driver-ID resolution, hardware compatibility, and bottleneck analysis.' },
  { title: 'Build & Upgrade Tools', text: 'PC build planning, gaming and workstation planning, upgrade recommendations, build spec sheets, and refurbished valuation. Any figures shown are planning estimates only, never quotes.' },
  { title: 'QA, Testing & Reports', text: 'Stress-test and burn-in tracking, QC and repair checklists, before/after and system-information summaries, backup verification, and handover summaries. Outputs are technician summaries, not customer documents.' },
  { title: 'Data & Security', text: 'A data recovery assessment (difficulty, likelihood, and handling risk, not a quote), a security audit checklist with a risk level, and a password-policy guidance generator.' },
  { title: 'External Tool References', text: 'A reference-only index of common external tools with purpose and status labels. VISINVOKE does not host, download, or launch third-party software.' },
  { title: 'Help', text: 'This guide, the quick-start, and the Tech Hub help and safe-usage page.' },
];

export const USER_GUIDE_NOTES = [
  'Forms, checklists, and reports are in-session only unless a future saved workflow is explicitly added.',
  'Tech Hub does not store customer passwords; the password tools produce guidance only.',
  'External Tool References is reference-only and does not host, download, or launch third-party software.',
  'On a client PC, sign in to use Tech Hub, then sign out when finished. The client PC is not attached to your account, and no monitoring, management, ticketing, or device ownership is created.',
];

export const TECH_HUB_HELP_SECTIONS = [
  { title: 'What Tech Hub is', text: 'Tech Hub is a set of technician utilities inside VISINVOKE. It runs only through a signed-in VISINVOKE account with Technician Access. It is not a standalone, offline, or USB toolkit.' },
  { title: 'Accounts and access', text: 'A signed-in VISINVOKE account is required, and technician tools require Technician Access. A client may later create their own VISINVOKE account to use free utilities; free means free in price, not anonymous access.' },
  { title: 'Working on a client PC', text: 'Sign in to VISINVOKE on the client PC, use Tech Hub in-session, then sign out. The client PC is never attached to your account.' },
];

export const TECH_HUB_HELP_SAFETY = [
  'No customer passwords are entered, displayed, or stored.',
  'No automatic device management, monitoring, RMM, or MSP behaviour.',
  'No local executable launching or file-system access.',
  'No third-party software is hosted, downloaded, or launched from the External Tool References index.',
  'Sign out of VISINVOKE after use on a client PC.',
  'Obtain external tools from their official vendor download pages and confirm licensing.',
];
